<script>
import axios from "axios";
import { formatAmount, formatCurrency, formatCurrencyCode, getLocaleForCurrency, resolveReservationCurrency } from "@/utils/formatters";
import ConfirmSendInvoiceLinkModal from "@/components/invoices/ConfirmSendInvoiceLinkModal.vue";
import ManualInvoiceCreateModal from "@/components/invoices/ManualInvoiceCreateModal.vue";
import ReservationPaymentsPanel from "@/components/Reservas/ReservationPaymentsPanel.vue";
import { $toast } from "@/utils/useToast";
import { bookBase } from "@/utils/bookBase";

export default {
  props: {
    reservationData: { type: Object, required: true },
    user:            { type: Object, default: null },
  },

  components: { ConfirmSendInvoiceLinkModal, ManualInvoiceCreateModal, ReservationPaymentsPanel },

  data() {
    return {
      dropdownOpen:            false,
      isPaymentModalOpen:      false,
      paymentStatus:           null,
      isSavingStatus:          false,
      isGeneratingLink:        false,
      isGeneratingInvoiceLink: false,
      showReceiptModal:        false,
      receiptData:             null,
      loadingReceipt:          false,
      waReceiptSending:        false,
      waReceiptSent:           false,
      waReceiptOutOfWindow:    false,
      showBreakdownModal:      false,
      extrasExpanded:          false,
      statusDropdownOpen:           false,
      showPartialModal:             false,
      partialAmount:                '',
      partialNote:                  '',
      isGeneratingPartialLink:      false,
      isSavingPartial:              false,
      partialPaymentUrl:            null,
      copiedPartialLink:            false,
      isSendingPaymentWhatsapp:     false,
      waAsyncState:                 null, // null | 'sending' | 'sent' | 'error'
      waAsyncMessage:               '',
      otaNetInfoOpen:               false, // explicación de «Te llega (aprox.)» desplegada
    };
  },

  computed: {
    billingProfile() {
      // Multi-emisor: el emisor aplicable a ESTA reserva (asignado a su
      // propiedad o el principal), inyectado por el backend en la reserva.
      // Fallback al perfil único antiguo por compatibilidad.
      return (
        this.reservationData?.billing_emisor ||
        this.user?.billing_profile ||
        this.user?.billingProfile ||
        null
      );
    },
    billingMissingFields() {
      const bp = this.billingProfile;
      if (!bp) return ["billing_profile"];
      // El email del emisor NO es obligatorio para facturar (GoblBuilder solo lo
      // incluye si existe). Solo exigimos los datos legales del emisor.
      const required = ["country","legal_name","tax_id","address_street","address_city","address_region","address_zip"];
      const missing  = [];
      required.forEach((k) => {
        const v = bp?.[k];
        if (v === null || v === undefined || String(v).trim() === "") missing.push(k);
      });
      if (bp?.is_active === false) missing.push("is_active");
      return missing;
    },
    hasBillingProfileComplete() { return this.billingMissingFields.length === 0; },
    hasElectronicInvoicing()    { return this.user?.servicios?.electronic_invoicing === true; },
    canUseInvoicing()           { return this.hasBillingProfileComplete && this.hasElectronicInvoicing; },

    // ✅ El recibo NO requiere billing profile — solo que exista la reserva
    canViewReceipt() { return !!this.reservationData?.id; },

    hasStripe()          { return !!this.user?.id_cliente_stripe; },
    hasWompi() {
      const acc = this.user?.user_payment_account;
      return acc?.provider === "wompi" && !!acc?.is_verified;
    },
    hasPaymentProvider() { return this.hasStripe || this.hasWompi; },
    canGeneratePaymentLink() {
      return parseFloat(this.reservationData?.price || 0) > 0 && this.hasPaymentProvider;
    },
    isPriceFree() { return parseFloat(this.reservationData?.price || 0) === 0; },

    currentPayStatus() {
      const pr  = this.reservationData?.payment_reservation;
      const pay = pr?.pay;
      if (pr && pay !== null && pay !== undefined) return Number(pay);
      const hasPrice = parseFloat(this.reservationData?.price || 0) > 0;
      if (hasPrice) return 0;
      return null;
    },

    statusOptions() {
      return [
        { value: 0, label: this.$t("booking.pending"),   icon: 'pi pi-clock',        color: '#f59e0b', bg: '#fff8e1', border: '#fde68a' },
        { value: 1, label: this.$t("booking.paidShort"), icon: 'pi pi-check-circle', color: '#019e35', bg: '#e4f2e4', border: '#a7f3d0' },
        { value: 4, label: this.$t("booking.partial"),   icon: 'pi pi-wallet',       color: '#e65100', bg: '#fff3e0', border: '#fed7aa' },
        { value: 2, label: this.$t("booking.rejected"),  icon: 'pi pi-times-circle', color: '#dc2626', bg: '#fde8e8', border: '#fca5a5' },
        { value: 3, label: this.$t("booking.refunded"),  icon: 'pi pi-history',      color: '#6b7280', bg: '#f3f4f6', border: '#d1d5db' },
      ];
    },
    selectedStatusOption() {
      return this.statusOptions.find(o => o.value === this.paymentStatus) ?? null;
    },

    payStatusConfig() {
      const map = {
        0: { label: this.$t("booking.paymentPending"), icon: "pi pi-clock",        bg: "#fff8e1", color: "#f59e0b", border: "#fde68a" },
        1: { label: this.$t("booking.paid"),           icon: "pi pi-check-circle", bg: "#e4f2e4", color: "#019e35", border: "#a7f3d0" },
        2: { label: this.$t("booking.rejected"),       icon: "pi pi-times-circle", bg: "#fde8e8", color: "#dc2626", border: "#fca5a5" },
        3: { label: this.$t("booking.refunded"),       icon: "pi pi-history",      bg: "#f3f4f6", color: "#6b7280", border: "#d1d5db" },
        4: { label: this.$t("booking.partial"),        icon: "pi pi-minus-circle", bg: "#fff3e0", color: "#e65100", border: "#fed7aa" },
      };
      return this.currentPayStatus !== null ? (map[this.currentPayStatus] ?? null) : null;
    },

    previewStatusConfig() {
      const map = {
        0: { label: this.$t("booking.pending"),   bg: "#fff8e1", color: "#f59e0b" },
        1: { label: this.$t("booking.paidShort"), bg: "#e4f2e4", color: "#019e35" },
        2: { label: this.$t("booking.rejected"),  bg: "#fde8e8", color: "#dc2626" },
        3: { label: this.$t("booking.refunded"),  bg: "#f3f4f6", color: "#6b7280" },
        4: { label: this.$t("booking.partial"),   bg: "#fff3e0", color: "#e65100" },
      };
      return this.paymentStatus !== null ? (map[this.paymentStatus] ?? null) : null;
    },

    // ── Recibo: computed de cálculos ──────────────────────────────────────
    receiptNights() {
      if (!this.receiptData) return 1;
      const ci   = this.parseLocalDate(this.receiptData.reservation.checkin);
      const co   = this.parseLocalDate(this.receiptData.reservation.checkout);
      if (!ci || !co) return 1;
      const diff = Math.round((co - ci) / (1000 * 60 * 60 * 24));
      return diff > 0 ? diff : 1;
    },
    receiptLodgingBase()   { return this.receiptData?.amounts?.lodging_base   ?? 0; },
    receiptLodgingPrice()  { return this.receiptData?.amounts?.lodging_price  ?? 0; },
    receiptLodgingTax()    { return this.receiptData?.amounts?.lodging_tax    ?? 0; },
    receiptChargesTotal()  { return this.receiptData?.amounts?.charges_total  ?? 0; },
    receiptGrandTotal()    { return this.receiptData?.amounts?.total          ?? 0; },
    isGroup()    { return Boolean(this.reservationData && this.reservationData.is_group); },
    groupTotal() { return Number((this.reservationData && this.reservationData.group_total) || 0); },
    groupRooms() { return Number((this.reservationData && this.reservationData.group_rooms) || 0); },
    receiptPaid()          { return this.receiptData?.amounts?.paid           ?? 0; },
    receiptPending()       { return this.receiptData?.amounts?.pending        ?? 0; },
    receiptPerNight()      { return this.receiptData?.amounts?.per_night      ?? 0; },
    receiptTaxRate()       { return this.receiptData?.amounts?.tax_rate       ?? 0; },
    receiptCurrency()      { return this.receiptData?.amounts?.currency       ?? "EUR"; },
    receiptHasCharges()    { return (this.receiptData?.charges ?? []).length > 0; },
    receiptNightLines()    { return this.receiptData?.night_lines    ?? null; },
    receiptDiscountLines() { return this.receiptData?.discount_lines ?? null; },
    receiptUseNightLines() { return Array.isArray(this.receiptNightLines) && this.receiptNightLines.length > 0; },
    // ── Pago parcial: datos existentes ───────────────────────────────────
    partialTotalPrice() {
      // El total contra el que se calcula el pago parcial incluye los cargos extra
      return this.getFinalTotal();
    },
    partialAmountParsed() {
      return parseFloat(this.partialAmount) || 0;
    },
    partialRemaining() {
      const r = this.partialTotalPrice - this.partialAmountParsed;
      return r > 0 ? r : 0;
    },
    partialIsValid() {
      return this.partialAmountParsed > 0 && this.partialAmountParsed < this.partialTotalPrice;
    },
    existingPartialAmount() {
      return parseFloat(this.reservationData?.payment_reservation?.partial_amount) || 0;
    },
    existingPartialNote() {
      return this.reservationData?.payment_reservation?.partial_note || '';
    },
    existingPartialPaymentUrl() {
      return this.reservationData?.payment_reservation?.partial_payment_url || null;
    },
    existingPartialRemaining() {
      return Math.max(0, this.partialTotalPrice - this.existingPartialAmount);
    },

    financialLines() {
      return Array.isArray(this.reservationData?.reservation_financial_lines)
        ? this.reservationData.reservation_financial_lines
        : [];
    },
    hasFinancialBreakdown() {
      return this.financialLines.length > 0;
    },
    /**
     * La moneda REAL de la reserva.
     *
     * 🚨 Manda el PAÍS del alojamiento, no la línea del desglose. Antes era al revés
     * —se cogía la moneda de la primera línea— y bastaba UNA línea con la moneda mal
     * para que el desglose entero cambiase de divisa: en demo, 27 de las 28 reservas
     * de un alojamiento de Málaga salían en LIBRAS porque sus líneas se habían
     * guardado en GBP. El cliente veía «£1.212,00» en una reserva en euros.
     *
     * El país es el dato fiable: resuelve también el caso que motivó el orden
     * anterior (una propiedad de Colombia con `currency` puesto a «EUR» por
     * arrastre), y no se deja engañar por una línea sucia. La línea queda de último
     * recurso, para cuando no hay país ni moneda en la propiedad.
     */
    /* 🚨 UNA sola fuente. Esto era una copia con otro orden de prioridades que el
       del panel de pagos, así que el MISMO bloque enseñaba «Subtotal 500,00 BRL»
       arriba y «TOTAL 500,00 EUR» abajo — dos monedas para el mismo dinero. El
       porqué del orden está documentado en `resolveReservationCurrency`. */
    resolvedCurrency() {
      return resolveReservationCurrency(this.reservationData);
    },
    breakdownNights() {
      return this.financialLines.filter(l =>
        ["room_night", "room_stay"].includes(this.lineCategory(l)) && this.lineAmount(l) !== 0
      );
    },
    // IVA de la reserva (snapshot congelado del anuncio). Va INCLUIDO en el precio.
    reservaIvaRate() {
      const s = parseFloat(this.reservationData?.tax_rate);
      return Number.isNaN(s) ? 0 : s;
    },
    // Importe de IVA ya incluido en el precio del alojamiento (no se suma al total).
    reservaIvaIncluded() {
      const rate = this.reservaIvaRate;
      if (rate <= 0) return 0;

      /* 🚨 NO solo las noches. Los servicios del anfitrión, el huésped extra y los
         ajustes van DENTRO del precio de la reserva y la factura los emite al mismo
         tipo que el alojamiento, así que su IVA también está incluido aquí.
         Contando solo las noches, una estancia de 750 € —500 de noches más 250 de
         un ajuste por extenderla— enseñaba 45,45 € de IVA, que es el de 500,
         mientras la factura declaraba 68,18. Otra vez el panel y la factura
         echando cuentas distintas del mismo dinero.
         El descuento va con su signo: resta de la base, como en la factura. */
      const base =
        this.breakdownNights.reduce((a, l) => a + this.lineAmount(l), 0)
        + this.sumFinancialLines(["service", "fee", "extra_guest", "adjustment"], false)
        + this.getDiscounts();

      return base * rate / (100 + rate);
    },
    // IVA/tasas ya INCLUIDAS en el precio (líneas tax incluidas + IVA por tarifa del anuncio).
    includedTaxesTotal() {
      return this.getIncludedTaxes() + this.reservaIvaIncluded;
    },
    // Total de impuestos a mostrar en el resumen "Importe de la reserva":
    // los añadidos sobre el precio MÁS los ya incluidos (IVA de la reserva).
    // Antes solo mostraba los añadidos → salía 0 con IVA incluido.
    totalTaxesDisplay() {
      return this.getTaxes() + this.includedTaxesTotal;
    },
    breakdownExtras() {
      return this.financialLines.filter(l =>
        ["service", "fee", "extra_guest", "adjustment"].includes(this.lineCategory(l)) &&
        !this.isIncludedLine(l) && this.lineAmount(l) !== 0
      );
    },
    // Servicios/extras que viven en invoice_charges (extras vendidos en el motor
    // p.ej. "Paseo por el campo", cargos del agente IA, cargos manuales). El TOTAL
    // (getFinalTotal) ya los suma; los normalizamos para itemizarlos también en el
    // desglose de precios y que las líneas visibles cuadren con el total.
    invoiceChargeLines() {
      const charges = Array.isArray(this.reservationData?.invoice_charges)
        ? this.reservationData.invoice_charges
        : [];
      return charges
        .map(c => ({
          description: c.description || c.title || this.$t("reservasSmallHc.payData.charge"),
          quantity: Number(c.quantity) || 1,
          amount: parseFloat(c.total_with_vat ?? c.total) || 0,
          room_name: c.room_name || null,
          // Servicio/extra elegido por el huésped en el motor: se muestra SIEMPRE,
          // aunque sea GRATIS (total 0) — p.ej. cuna o casa cueva "por disponibilidad".
          isService: String(c.category || "") === "service"
            || String(c.source || "") === "direct_booking_extra",
        }))
        // Los servicios/extras del huésped (incluidos los gratis) se muestran; el resto
        // de cargos con importe 0 se ocultan por ser ruido (ajustes, redondeos…).
        .filter(l => l.amount !== 0 || l.isService);
    },
    breakdownDiscounts() {
      return this.financialLines.filter(l =>
        this.lineCategory(l) === "discount" && this.lineAmount(l) !== 0
      );
    },
    // ── "Servicios y extras" del resumen: UNA sola línea agregada + desplegable ──
    // Fusiona los extras de financial_lines (servicios del quote) con los de
    // invoice_charges (extras del motor/agente/manuales, p.ej. "Paseo por el campo",
    // "Uber"). Antes salían en dos sitios: el agregado arriba y los individuales
    // sueltos DEBAJO del IVA → incoherente. Ahora el IVA queda al final de todo.
    allExtrasTotal() {
      return this.getExtrasTotal() + this.getManualChargesTotal();
    },
    extrasItems() {
      const fin = this.breakdownExtras.map(l => ({
        desc:   this.breakdownNightDesc(l),
        amount: this.lineAmount(l),
        qty:    Number(l.quantity) || 1,
      }));
      const chg = this.invoiceChargeLines.map(l => ({
        desc:   l.room_name ? `${l.description} · ${l.room_name}` : l.description,
        amount: l.amount,
        qty:    l.quantity || 1,
      }));
      return [...fin, ...chg];
    },
    hasAnyExtras() {
      return this.extrasItems.length > 0 || this.allExtrasTotal !== 0;
    },
    breakdownTaxes() {
      return this.financialLines.filter(l =>
        ["tax", "city_tax"].includes(this.lineCategory(l)) && this.lineAmount(l) !== 0
      );
    },
    // ¿Propiedad hotelera (con habitaciones) o alojamiento entero (apartamento)?
    isHotelProperty() {
      const type = String(this.reservationData?.propiedad?.type || "").toLowerCase();
      return type === "hotel" || type === "camping";
    },

    // ── Lo que te llega de una reserva de OTA ─────────────────────────────
    // Alexis (7-oct-2026), con una reserva de Booking de 705,35 €: Booking se quedó
    // 114,97 € de «comisión y cargos» y 24,14 € de IVA de esa comisión, y transfirió
    // 566,23 €. Aquí solo se veían los 705,35 y al anfitrión «no le llega lo que dice».
    // Las cuentas las hace el backend (`ota_net` de /retrieve/booking, NetoDeLaReserva)
    // y llega a null cuando no aplica: directa, iCal, sin precio o el anuncio no encendió
    // «también lo que te llega» en «Comisiones de las OTAs». Aquí solo se pinta.
    otaNet() {
      const net = this.reservationData?.ota_net;
      if (!net || typeof net !== "object") return null;
      // Una cancelada no liquida lo de siempre (depende de la política y de la OTA):
      // decir «te llega 566 €» sería inventárselo. El backend no mira el estado.
      const status = String(this.reservationData?.status || "").toLowerCase();
      if (["cancelled", "canceled", "cancelado", "cancelada"].includes(status)) return null;
      return net;
    },
    otaNetChannel() {
      return this.otaNet?.channel_name || this.$t("otaNet.theChannel");
    },
    // Airbnb paga el «Payout Amount»: lo que importamos ya llega sin su comisión y no
    // hay nada que restar. Si el anfitrión le puso un % (comisión > 0), es que su
    // conexión recibe el total, y se enseñan las líneas como en cualquier otro canal.
    otaNetAirbnbNet() {
      return !!this.otaNet?.airbnb_net_amount && !(Number(this.otaNet.commission) > 0);
    },
    // 'real' = el canal mandó la comisión de ESTA reserva; 'estimate' = con el % de la tarjeta.
    otaNetIsReal() {
      return this.otaNet?.commission_source === "real";
    },
    otaNetHasVat() {
      return Number(this.otaNet?.vat) > 0;
    },
    // La tasa turística y la fianza no pagan comisión: si la base no es el total, hay
    // que decir sobre cuánto se calcula, o el 16,3 % de 705,35 no le cuadra a nadie.
    otaNetBaseDiffers() {
      const n = this.otaNet;
      if (!n || this.otaNetIsReal) return false;
      const base = Number(n.commission_base);
      return Number.isFinite(base) && Math.abs(base - (Number(n.gross) || 0)) >= 0.01;
    },
    // Lo que se cobra aparte (extras, salida tardía, una ampliación pendiente…) está en
    // el total de arriba pero NO pasa por la OTA: el backend descuenta sobre el precio
    // de la reserva (`reservas.price`). La diferencia, para decirlo si la hay.
    otaNetApart() {
      if (!this.otaNet || this.isGroup) return 0;
      return Math.round((this.getFinalTotal() - (Number(this.otaNet.gross) || 0)) * 100) / 100;
    },
    otaNetGrossLabel() {
      if (!this.otaNet) return "";
      // Reserva combinada: el bloque de arriba es el del GRUPO, pero el neto es de ESTA
      // línea (el backend lo calcula sobre su precio). Que no pase por el total del grupo.
      if (this.isGroup) {
        const rd = this.reservationData || {};
        const own = (Array.isArray(rd.group_lines) ? rd.group_lines : [])
          .find(l => Number(l?.reserva_id) === Number(rd.id));
        const unit = own?.unit_name || rd.accommodation_unit?.name || rd.propiedad?.nombre;
        if (unit) return this.$t("otaNet.grossOfUnit", { unit });
      }
      if (Math.abs(this.otaNetApart) >= 0.01) {
        return this.$t("otaNet.grossOnChannel", { channel: this.otaNetChannel });
      }
      return this.$t("otaNet.gross");
    },
    otaNetCommissionLabel() {
      const n = this.otaNet;
      if (!n) return "";
      const channel = this.otaNetChannel;
      // La comisión real no tiene «%» que enseñar: es la que mandó el canal.
      if (this.otaNetIsReal) return this.$t("otaNet.commissionReal", { channel });
      const rate = this.otaPct(n.commission_rate);
      if (this.otaNetBaseDiffers) {
        return this.$t("otaNet.commissionOfBase", { channel, rate, base: this.otaIsolate(this.format(n.commission_base)) });
      }
      return this.$t("otaNet.commission", { channel, rate });
    },
    // El porqué del «aprox.»: la estimación sale de los % de la tarjeta; con la comisión
    // real, lo aproximado es el IVA (si lo hay) y lo que acabe transfiriendo la OTA.
    otaNetInfo() {
      if (!this.otaNet) return "";
      const channel = this.otaNetChannel;
      if (this.otaNetIsReal) {
        return this.otaNetHasVat
          ? this.$t("otaNet.infoReal", { channel })
          : this.$t("otaNet.infoRealNoVat", { channel });
      }
      const text = this.$t("otaNet.infoEstimate");
      return this.otaNetBaseDiffers ? `${text} ${this.$t("otaNet.infoBase")}` : text;
    },
    // id del texto que despliega el botón (aria-controls): uno por instancia.
    otaNetInfoId() {
      return `pay-net-info-${this._uid}`;
    },
  },

  methods: {
    // ── Pagos (líneas) → sincroniza el badge de estado derivado ───────────
    onPaymentsChanged(totals) {
      if (!this.reservationData || !totals || totals.pay == null) return;
      if (this.reservationData.payment_reservation) {
        this.$set(this.reservationData.payment_reservation, 'pay', totals.pay);
      } else {
        this.$set(this.reservationData, 'payment_reservation', { pay: totals.pay });
      }
    },

    // ── Dropdown ──────────────────────────────────────────────────────────
    toggleDropdown() { this.dropdownOpen = !this.dropdownOpen; },
    closeDropdown()  { this.dropdownOpen = false; },

    // ── Montos ────────────────────────────────────────────────────────────
    /* El CÓDIGO de la moneda en letras («1.212,00 EUR»), no el símbolo. Es lo que se
       usa en el resto de la ficha y en las tarjetas de abajo, y además evita el lío de
       los símbolos repetidos: $ es dólar, peso colombiano y peso mexicano a la vez. */
    format(v)           { return formatCurrencyCode(v, this.resolvedCurrency); },
    // Ponía el código DELANTE («EUR 1.212,00») mientras el resto de la ficha lo pone
    // detrás. Una sola forma de escribir un importe.
    fmtCode(v) { return formatCurrencyCode(v, this.resolvedCurrency); },
    // «Lo que te llega»: lo que se descuenta va con el signo menos de verdad (−), como
    // en la liquidación de los gestores; un cero sale sin signo.
    otaMinus(v) {
      const n = Math.round((Number(v) || 0) * 100) / 100;
      if (n === 0) return this.format(0);
      return (n > 0 ? "−" : "+") + this.format(Math.abs(n));
    },
    // El % con los separadores de la moneda, como los importes de al lado: «16,3 %»
    // junto a «−114,97 €» (y no «16.3 %»).
    otaPct(v) {
      const n = Number(v) || 0;
      try {
        return new Intl.NumberFormat(getLocaleForCurrency(this.resolvedCurrency), { maximumFractionDigits: 2 }).format(n);
      } catch (e) {
        return String(n);
      }
    },
    // Aísla un importe metido en una frase: en árabe (RTL) «24,14 €» se partía y el
    // símbolo saltaba al otro lado de la cifra.
    otaIsolate(text) {
      return "\u2066" + text + "\u2069";
    },
    getFormattedPrice() {
      return formatCurrencyCode(this.getFinalTotal(), this.resolvedCurrency);
    },
    getDiscounts() {
      if (this.hasFinancialBreakdown) {
        return this.sumFinancialLines(["discount"]);
      }
      const legacy = this.reservationData?.payment_reservation?.payment_reservation_promotions_taxes
        ?.filter(i => i.type === "promotion")
        .reduce((a, p) => a + (parseFloat(p.price) || 0), 0) || 0;
      return -legacy;
    },
    getTaxes() {
      if (this.hasFinancialBreakdown) {
        return this.sumFinancialLines(["tax", "city_tax"], false);
      }
      return this.reservationData?.payment_reservation?.payment_reservation_promotions_taxes
        ?.filter(i => i.type === "taxes").reduce((a, t) => a + (parseFloat(t.price) || 0), 0) || 0;
    },
    getSubtotal() {
      if (this.hasFinancialBreakdown) {
        return this.sumFinancialLines(["room_night", "room_stay"], false);
      }
      return parseFloat(this.reservationData?.price || 0) - this.getTaxes() - this.getDiscounts();
    },
    getExtrasTotal() {
      if (!this.hasFinancialBreakdown) return 0;
      return this.sumFinancialLines(["service", "fee", "extra_guest", "adjustment"], false);
    },
    getIncludedTaxes() {
      if (!this.hasFinancialBreakdown) return 0;
      return this.sumFinancialLines(["tax", "city_tax"], true);
    },
    getBreakdownTotal() {
      if (!this.hasFinancialBreakdown) {
        return parseFloat(this.reservationData?.price || 0);
      }
      return this.financialLines
        .filter(line => !this.isIncludedLine(line))
        .reduce((sum, line) => sum + this.lineAmount(line), 0);
    },
    // Suma de cargos extra sin facturar (limpieza, parking, etc.) con IVA incluido
    getManualChargesTotal() {
      const charges = this.reservationData?.invoice_charges || [];
      return charges.reduce(
        (sum, c) => sum + (parseFloat(c.total_with_vat ?? c.total) || 0),
        0
      );
    },
    // Total real a cobrar: alojamiento + cargos extra.
    // En reserva compuesta = total del GRUPO (todas las habitaciones); el desglose
    // (que ahora trae las líneas del grupo etiquetadas por habitación) cuadra con él.
    getFinalTotal() {
      if (this.isGroup && this.groupTotal) return this.groupTotal;
      if (this.reservationData?.total_to_pay != null) {
        return parseFloat(this.reservationData.total_to_pay) || 0;
      }
      return this.getBreakdownTotal() + this.getManualChargesTotal();
    },
    getVisibleFinancialLines() {
      return this.financialLines.filter(line => {
        if (!line) return false;
        if (this.lineAmount(line) === 0) return false;
        return ["room_night", "room_stay", "service", "fee", "extra_guest", "discount", "tax", "city_tax", "adjustment"].includes(this.lineCategory(line));
      });
    },
    lineAmount(line) {
      return parseFloat(line?.amount || 0) || 0;
    },
    lineCategory(line) {
      return String(line?.category || "").toLowerCase();
    },
    isIncludedLine(line) {
      return Boolean(line?.is_included);
    },
    sumFinancialLines(categories = [], onlyIncluded = false) {
      return this.financialLines
        .filter(line => categories.includes(this.lineCategory(line)) && this.isIncludedLine(line) === onlyIncluded)
        .reduce((sum, line) => sum + this.lineAmount(line), 0);
    },
    formatBreakdownLineAmount(line) {
      return formatCurrencyCode(this.lineAmount(line), this.resolvedCurrency);
    },
    // En apartamentos, "Habitación N" no aplica: mostramos el nombre del alojamiento.
    breakdownNightDesc(line) {
      const desc = String(line?.description || "");
      // Cantidad de habitaciones (reserva compuesta): "(×2)" cuando aplica.
      const qty = Number(line?.quantity) || 1;
      const qtyLabel = qty > 1 ? ` (×${qty})` : "";
      if (this.isHotelProperty) {
        // En hotel, prefija la HABITACIÓN de la línea (Duplex/Simple).
        const room = line?.room_name ? `${line.room_name} · ` : "";
        return `${room}${desc}${qtyLabel}`;
      }
      const name = this.reservationData?.propiedad?.nombre || this.$t("reservasSmallHc.payData.lodging");
      return desc.replace(/^\s*Habitaci[oó]n\s*\d+/i, name) + qtyLabel;
    },
    getBreakdownLineClass(line) {
      const amount = this.lineAmount(line);
      if (amount < 0) return "pay-breakdown__value pay-breakdown__value--discount";
      if (this.isIncludedLine(line)) return "pay-breakdown__value pay-breakdown__value--included";
      return "pay-breakdown__value";
    },

    // ── Modal estado pago ─────────────────────────────────────────────────
    openModal()  { this.closeDropdown(); this.paymentStatus = null; this.statusDropdownOpen = false; this.isPaymentModalOpen = true; },
    closeModal() { this.isPaymentModalOpen = false; this.statusDropdownOpen = false; },

    async changePaymentReservation() {
      if (this.paymentStatus === null) return;

      // Redirect to the partial modal when "Pago Parcial" is selected
      if (Number(this.paymentStatus) === 4) {
        this.closeModal();
        this.openPartialModal();
        return;
      }

      const pr            = this.reservationData?.payment_reservation;
      const paymentId     = pr?.id   ?? null;
      const reservationId = this.reservationData?.id ?? null;
      if (!paymentId && !reservationId) { $toast.error(this.$t("reservasSmallHc.payData.errorNoPaymentRecord")); return; }
      const wasPartial = this.currentPayStatus === 4;
      this.isSavingStatus = true;
      try {
        const payload = { pay: Number(this.paymentStatus), reservation_id: reservationId };
        if (paymentId) payload.id = paymentId;
        const { data } = await axios.put("/change/payment-reservation", payload);
        if (this.reservationData?.payment_reservation) {
          this.reservationData.payment_reservation.pay = data.pay ?? Number(this.paymentStatus);
          if (data.id && !this.reservationData.payment_reservation.id) {
            this.reservationData.payment_reservation.id = data.id;
          }
        } else {
          this.$set(this.reservationData, "payment_reservation", { pay: Number(this.paymentStatus), id: data.id });
        }
        this.closeModal();
        $toast.success(this.$t("booking.statusUpdatedSuccess"));

        // Restaurar el link de pago al precio total si salimos del estado parcial
        if (wasPartial && this.hasPaymentProvider) {
          const fullPrice = parseFloat(this.reservationData?.price || 0);
          if (fullPrice > 0) {
            try { await this.validateAndGeneratePaymentLink(fullPrice); } catch { /* silent */ }
          }
        }
      } catch (err) {
        $toast.error(err?.response?.data?.message || this.$t("booking.statusUpdatedError"));
      } finally {
        this.isSavingStatus = false;
      }
    },

    onStatusOptionClick(opt) {
      this.paymentStatus       = opt.value;
      this.statusDropdownOpen  = false;
      if (opt.value === 4) {
        this.closeModal();
        this.openPartialModal();
      }
    },

    // ── Pago parcial ─────────────────────────────────────────────────────
    openPartialModal() {
      this.closeDropdown();
      this.partialAmount     = this.existingPartialAmount ? String(this.existingPartialAmount) : '';
      this.partialNote       = this.existingPartialNote;
      this.partialPaymentUrl = this.existingPartialPaymentUrl;
      this.showPartialModal  = true;
    },
    closePartialModal() { this.showPartialModal = false; },

    async generatePartialLink() {
      if (!this.partialIsValid || !this.reservationData?.id) return;
      this.isGeneratingPartialLink = true;
      try {
        const { data } = await axios.post(
          `/reservas/${this.reservationData.id}/partial-payment-link`,
          { partial_amount: this.partialAmountParsed, note: this.partialNote || null }
        );
        this.partialPaymentUrl = data.partial_payment_url;
        if (this.reservationData?.payment_reservation) {
          this.reservationData.payment_reservation.partial_payment_url = data.partial_payment_url;
        }
        $toast.success(this.$t("reservasSmallHc.payData.partialLinkGenerated"));
      } catch (e) {
        $toast.error(e?.response?.data?.message || this.$t("reservasSmallHc.payData.errorGenerateLink"));
      } finally {
        this.isGeneratingPartialLink = false;
      }
    },

    async savePartialPayment() {
      if (!this.partialIsValid) return;
      this.isSavingPartial = true;
      try {
        const pr            = this.reservationData?.payment_reservation;
        const paymentId     = pr?.id ?? null;
        const reservationId = this.reservationData?.id ?? null;
        const payload = {
          pay:            4,
          partial_amount: this.partialAmountParsed,
          partial_note:   this.partialNote || '',
          reservation_id: reservationId,   // siempre incluido como fallback
        };
        if (paymentId) payload.id = paymentId;

        const { data } = await axios.put("/change/payment-reservation", payload);

        // Fix reactivity in Vue 2: use $set for each field
        if (this.reservationData?.payment_reservation) {
          const pr2 = this.reservationData.payment_reservation;
          this.$set(pr2, 'pay',            4);
          this.$set(pr2, 'partial_amount', this.partialAmountParsed);
          this.$set(pr2, 'partial_note',   this.partialNote || '');
          if (data.id && !pr2.id) this.$set(pr2, 'id', data.id);
        } else {
          this.$set(this.reservationData, 'payment_reservation', {
            id:             data.id,
            pay:            4,
            partial_amount: this.partialAmountParsed,
            partial_note:   this.partialNote || '',
          });
        }

        this.closePartialModal();
        $toast.success(this.$t("reservasSmallHc.payData.partialRegistered"));

        // Auto-actualizar el link de pago al monto restante
        if (this.hasPaymentProvider && this.partialRemaining > 0) {
          try {
            await this.validateAndGeneratePaymentLink(this.partialRemaining);
          } catch { /* no bloquear si falla el link */ }
        }
      } catch (e) {
        $toast.error(e?.response?.data?.message || this.$t("reservasSmallHc.payData.errorSavePartial"));
      } finally {
        this.isSavingPartial = false;
      }
    },

    async copyPartialLink(url) {
      if (!url) return;
      await this.copyToClipboard(url);
      this.copiedPartialLink = true;
      setTimeout(() => { this.copiedPartialLink = false; }, 2500);
    },
    sendPartialLinkWhatsApp(url) {
      if (!url) return;
      const name = this.reservationData?.nombre || '';
      const msg  = this.$t("reservasSmallHc.payData.whatsappPartialMsg", { name, url });
      window.open(`https://wa.me/${this.reservationData?.telefono}?text=${encodeURIComponent(msg)}`, '_blank');
    },

    // ── Billing / Factura ─────────────────────────────────────────────────
    ensureBillingProfileOrWarn() {
      if (this.canUseInvoicing) return true;
      if (!this.hasElectronicInvoicing) {
        $toast.warning(this.$t("reservasSmallHc.payData.errorInvoicingNotActive")); return false;
      }
      const map = {
        billing_profile: this.$t("reservasSmallHc.payData.fieldBillingProfile"),
        country: this.$t("reservasSmallHc.payData.fieldCountry"),
        legal_name: this.$t("reservasSmallHc.payData.fieldLegalName"),
        tax_id: this.$t("reservasSmallHc.payData.fieldTaxId"),
        address_street: this.$t("reservasSmallHc.payData.fieldAddressStreet"),
        address_city: this.$t("reservasSmallHc.payData.fieldAddressCity"),
        address_region: this.$t("reservasSmallHc.payData.fieldAddressRegion"),
        address_zip: this.$t("reservasSmallHc.payData.fieldAddressZip"),
        email: this.$t("reservasSmallHc.payData.fieldEmail"),
        is_active: this.$t("reservasSmallHc.payData.fieldIsActive"),
      };
      $toast.warning(this.$t("reservasSmallHc.payData.errorBillingIncomplete", { fields: this.billingMissingFields.map(k => map[k]||k).join(", ") }));
      return false;
    },
    openConfirmInvoiceModal() {
      if (!this.ensureBillingProfileOrWarn() || !this.reservationData?.id) return;
      this.$refs.invoiceModal?.open();
    },
    openManualInvoiceModal() {
      if (!this.ensureBillingProfileOrWarn() || !this.reservationData?.id) return;
      this.$refs.manualInvoiceModal?.open();
    },
    handleManualInvoiceCreated(invoice) {
      const bp = this.billingProfile;
      const nextNumber =
        invoice?.billing?.sequence_number != null
          ? Number(invoice.billing.sequence_number) + 1
          : Number(bp?.invoice_series_next_number || 1) + 1;

      if (bp) {
        bp.invoice_series_next_number = nextNumber;
      }
    },
    async copyInvoiceLinkDirect() {
      if (!this.ensureBillingProfileOrWarn() || !this.reservationData?.id) return;
      this.isGeneratingInvoiceLink = true;
      try {
        const { data } = await axios.post(`/reservas/${this.reservationData.id}/invoices/send-link`, { send: false });
        if (data?.url) { await this.copyToClipboard(data.url); $toast.success(this.$t("reservasSmallHc.payData.invoiceLinkCopied")); }
      } catch (e) {
        if (e?.response?.status === 409) {
          $toast.warning(this.$t("reservasSmallHc.payData.reservationHasInvoice"));
          const pdfUrl = e?.response?.data?.pdf_url;
          if (pdfUrl) window.open(pdfUrl, "_blank");
        } else {
          $toast.error(e?.response?.data?.message || this.$t("reservasSmallHc.payData.errorGenerateLink"));
        }
      } finally { this.isGeneratingInvoiceLink = false; }
    },

    // ── Links pago ────────────────────────────────────────────────────────
    async validateAndGeneratePaymentLink(forceAmount = null) {
      this.isGeneratingLink = true;
      try {
        let priceToUse;
        if (forceAmount !== null && forceAmount !== undefined) {
          priceToUse = forceAmount;
        } else if (this.currentPayStatus === 4 && this.existingPartialRemaining > 0) {
          // Pago parcial activo → el link debe ser por el saldo pendiente, no el total
          priceToUse = this.existingPartialRemaining;
        } else {
          priceToUse = parseFloat(this.reservationData?.price || 0);
        }
        const res = await axios.post("/validate-payment-link", {
          reservation_id: this.reservationData?.id,
          current_price:  priceToUse,
          promotions: this.reservationData?.payment_reservation?.payment_reservation_promotions_taxes?.filter(i => i.type === "promotion") || [],
          taxes:      this.reservationData?.payment_reservation?.payment_reservation_promotions_taxes?.filter(i => i.type === "taxes") || [],
        });
        if (res.data.success) {
          if (res.data.payment_url) {
            if (!this.reservationData.payment_reservation) this.reservationData.payment_reservation = {};
            this.reservationData.payment_reservation.payment_url = res.data.payment_url;
          }
          // Actualizar el id del PaymentReservation si fue recreado por el servicio
          if (res.data.payment_reservation_id && this.reservationData.payment_reservation) {
            this.$set(this.reservationData.payment_reservation, 'id', res.data.payment_reservation_id);
          }
          if      (res.data.generated)   $toast.success(this.$t("booking.linkGenerated"));
          else if (res.data.regenerated) $toast.success(this.$t("booking.linkRegenerated"));
          else                           $toast.success(this.$t("booking.linkValid"));
          return bookBase() + `/booking/${this.reservationData.uid}/confirm-payment`;
        } else {
          if      (res.data.price_zero)                         $toast.warning(this.$t("booking.linkPriceZero"));
          else if (res.data.no_provider || res.data.no_stripe) $toast.error(this.$t("booking.linkNoProvider"));
          else                                                  $toast.error(res.data.message || this.$t("booking.linkValidateError"));
          return null;
        }
      } catch (err) {
        $toast.error(err?.response?.data?.message || this.$t("booking.linkValidateError"));
        return null;
      } finally { this.isGeneratingLink = false; }
    },
    async copyPaymentLink() {
      this.closeDropdown();
      const url = await this.validateAndGeneratePaymentLink();
      if (url) { await this.copyToClipboard(url); $toast.success(this.$t("booking.copySuccess")); }
    },
    async sendPaymentLinkWhatsApp() {
      this.closeDropdown();
      this.isSendingPaymentWhatsapp = true;
      this.waAsyncState   = 'sending';
      this.waAsyncMessage = this.$t("booking.sendingPaymentLink");

      try {
        // 1) Asegurar que el link esté actualizado
        const url = await this.validateAndGeneratePaymentLink();
        if (!url) {
          this.waAsyncState   = 'error';
          this.waAsyncMessage = this.$t("booking.linkValidateError");
          return;
        }

        // 2) Enviar plantilla por WhatsApp (idioma auto-detectado por código de área)
        try {
          await axios.post(`/reservas/${this.reservationData.id}/send-payment-link-whatsapp`);
          this.waAsyncState   = 'sent';
          this.waAsyncMessage = this.$t("booking.whatsappSent");
        } catch (e) {
          // Fallback: abrir wa.me manualmente
          const waMsg = this.$t("booking.whatsappPayMessage", { name: this.reservationData?.nombre || "", url });
          window.open(`https://wa.me/${this.reservationData?.telefono}?text=${encodeURIComponent(waMsg)}`, "_blank");
          this.waAsyncState   = 'error';
          this.waAsyncMessage = this.$t("booking.whatsappSentFallback");
        }
      } finally {
        this.isSendingPaymentWhatsapp = false;
        // Ocultar el indicador flotante después de 4 segundos
        setTimeout(() => { this.waAsyncState = null; }, 4000);
      }
    },
    async copyToClipboard(text) {
      try { await navigator.clipboard.writeText(text); }
      catch {
        const ta = document.createElement("textarea");
        ta.value = text; document.body.appendChild(ta); ta.select();
        document.execCommand("copy"); document.body.removeChild(ta);
      }
    },

    // ── Recibo ────────────────────────────────────────────────────────────
    async openReceiptModal() {
      if (!this.reservationData?.id) return;
      this.showReceiptModal    = true;
      this.loadingReceipt      = true;
      this.receiptData         = null;
      this.waReceiptSent       = false;
      this.waReceiptOutOfWindow = false;
      try {
        const { data } = await axios.get(`/reservas/${this.reservationData.id}/receipt`);
        this.receiptData = data.data;
      } catch {
        $toast.error(this.$t("reservasSmallHc.payData.errorLoadReceipt"));
        this.showReceiptModal = false;
      } finally {
        this.loadingReceipt = false;
      }
    },
    closeReceiptModal() { this.showReceiptModal = false; },

    async downloadReceipt() {
      if (!this.reservationData?.id) return;
      try {
        const response = await axios.get(
          `${process.env.VUE_APP_API}/reservas/${this.reservationData.id}/receipt/pdf`,
          { responseType: "blob" }
        );
        const url  = URL.createObjectURL(new Blob([response.data], { type: "application/pdf" }));
        const link = document.createElement("a");
        link.href  = url;
        link.download = `recibo-${this.reservationData.codigo_reserva || this.reservationData.id}.pdf`;
        document.body.appendChild(link); link.click(); document.body.removeChild(link);
        URL.revokeObjectURL(url);
      } catch { $toast.error(this.$t("reservasSmallHc.payData.errorDownloadReceipt")); }
    },

    async sendReceiptWhatsapp() {
      if (!this.reservationData?.id) return;
      this.waReceiptSending     = true;
      this.waReceiptSent        = false;
      this.waReceiptOutOfWindow = false;
      try {
        await axios.post(`/reservas/${this.reservationData.id}/receipt/send-whatsapp`);
        this.waReceiptSent = true;
        setTimeout(() => { this.waReceiptSent = false; }, 5000);
      } catch (e) {
        const body = e?.response?.data;
        if (body?.out_of_window) {
          this.waReceiptOutOfWindow = true;
          $toast.warning(this.$t("reservasSmallHc.payData.guestOutOfWindow"));
        } else {
          $toast.error(body?.message || this.$t("reservasSmallHc.payData.errorSendReceipt"));
        }
      } finally { this.waReceiptSending = false; }
    },

    // ── Formatters ────────────────────────────────────────────────────────
    parseLocalDate(s) {
      if (!s) return null;
      const [y, m, d] = s.split("-").map(Number);
      return new Date(y, m - 1, d);
    },
    formatReceiptDate(s) {
      const d = this.parseLocalDate(s);
      return d ? d.toLocaleDateString("es-ES", { day: "2-digit", month: "long", year: "numeric" }) : "—";
    },
    formatMoney(amount) {
      const currency = this.receiptCurrency;
      const codeOnly = ["COP", "ARS", "CLP", "PEN"];
      const n = new Intl.NumberFormat("es-CO", {
        minimumFractionDigits: 2, maximumFractionDigits: 2,
      }).format(parseFloat(amount) || 0);
      return codeOnly.includes(currency) ? `${currency} ${n}` : `${currency} ${n}`;
    },
  },
};
</script>

<template>
  <div class="data-guest" style="margin-top: 30px">

    <!-- ══ Cabecera ══════════════════════════════════════════════════════════ -->
    <div class="paydata-header">
      <small class="box-title">{{ $t("booking.amountTitle") }}</small>

      <!-- Pagos gestionados por ReservationPaymentsPanel (Agregar pago…) -->
    </div>

    <!-- ══ Bloque totales ════════════════════════════════════════════════════ -->
    <div class="pay-totals-block">
      <div v-if="payStatusConfig" class="pay-status-badge"
        :style="{ background: payStatusConfig.bg, color: payStatusConfig.color, border: `1px solid ${payStatusConfig.border}` }">
        <i :class="payStatusConfig.icon"></i>
        <span>{{ payStatusConfig.label }}</span>
      </div>

      <!-- Reserva combinada: este bloque es el importe de ESTA habitación; el total
           del grupo (todas las habitaciones) se ve en el panel de arriba. -->
      <div v-if="isGroup" class="pay-group-note">
        <i class="fa-solid fa-layer-group"></i>
        <span>
          {{ $t("reservasSmallHc.payData.groupNotePre") }} <strong>{{ $t("reservasSmallHc.payData.groupNoteCombined") }}</strong>
          ({{ $tc("reservasSmallHc.payData.roomsCount", groupRooms, { count: groupRooms }) }}) — {{ $t("reservasSmallHc.payData.groupNoteMid") }}
          {{ $t("reservasSmallHc.payData.total") }} <strong>{{ format(groupTotal) }}</strong>.
        </span>
      </div>

      <div class="pay-row">
        <span class="pay-row__label">{{ $t("booking.subtotal") }}</span>
        <span class="pay-row__value">{{ format(getSubtotal()) }}</span>
      </div>
      <!-- Servicios y extras: UNA línea agregada + desplegable (clic para itemizar) -->
      <template v-if="hasAnyExtras">
        <div
          class="pay-row pay-row--expandable"
          :class="{ 'pay-row--expandable-open': extrasExpanded }"
          role="button"
          tabindex="0"
          @click="extrasExpanded = !extrasExpanded"
          @keydown.enter.prevent="extrasExpanded = !extrasExpanded"
          @keydown.space.prevent="extrasExpanded = !extrasExpanded"
        >
          <span class="pay-row__label pay-row__label--toggle">
            <svg class="pay-row__caret" :class="{ 'pay-row__caret--open': extrasExpanded }"
              width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.6">
              <polyline points="9 18 15 12 9 6"/>
            </svg>
            {{ $t("reservasSmallHc.payData.servicesExtras") }}
            <small v-if="extrasItems.length" class="pay-row__count">{{ extrasItems.length }}</small>
          </span>
          <span class="pay-row__value">{{ format(allExtrasTotal) }}</span>
        </div>
        <transition name="extras-expand">
          <div v-if="extrasExpanded" class="pay-extras-detail">
            <div v-for="(item, i) in extrasItems" :key="'extra-' + i" class="pay-extra-line">
              <span class="pay-extra-line__desc">
                {{ item.desc }}<small v-if="item.qty > 1" class="pay-row__qty">× {{ item.qty }}</small>
              </span>
              <span v-if="Number(item.amount) === 0" class="pay-extra-line__amount" style="color:#16a34a;font-weight:600;">{{ $t("reservasSmallHc.payData.free") }}</span>
              <span v-else class="pay-extra-line__amount">{{ format(item.amount) }}</span>
            </div>
          </div>
        </transition>
      </template>
      <div class="pay-row">
        <span class="pay-row__label">{{ $t("booking.totalDiscounts") }}</span>
        <span class="pay-row__value">{{ format(getDiscounts()) }}</span>
      </div>
      <div class="pay-row">
        <span class="pay-row__label">
          {{ $t("booking.totalTaxes") }}
          <small v-if="includedTaxesTotal > 0" class="pay-row__hint">{{ reservaIvaRate > 0 ? $t("reservasSmallHc.payData.ivaIncluded", { rate: reservaIvaRate }) : $t("reservasSmallHc.payData.includedInPrice") }}</small>
        </span>
        <span class="pay-row__value">{{ format(totalTaxesDisplay) }}</span>
      </div>

      <!-- ══ Lo que te llega (reserva de OTA) ══
           Continúa el desglose: total − comisión de la OTA − IVA de esa comisión = lo que
           le llega de verdad al anfitrión. Solo si el anuncio lo encendió en «Comisiones
           de las OTAs» (si no, `ota_net` viene a null y aquí no sale nada). Los importes
           van con dir="ltr" para que en árabe no se partan cifra y símbolo. -->
      <template v-if="otaNet">
        <div class="pay-divider"></div>
        <div class="pay-net">
          <span class="pay-net__title">{{ $t("otaNet.title") }}</span>

          <!-- Airbnb ya paga neto: no hay nada que restar, basta con decirlo -->
          <p v-if="otaNetAirbnbNet" class="pay-net__note">
            <svg class="pay-net__note-ic" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
              <circle cx="12" cy="12" r="10"/><path d="M12 16v-4"/><path d="M12 8h.01"/>
            </svg>
            <span>{{ $t("otaNet.airbnbNet") }}</span>
          </p>

          <template v-else>
            <div class="pay-row pay-row--net">
              <span class="pay-row__label">{{ otaNetGrossLabel }}</span>
              <span class="pay-row__value" dir="ltr">{{ format(otaNet.gross) }}</span>
            </div>
            <div class="pay-row pay-row--net">
              <span class="pay-row__label">{{ otaNetCommissionLabel }}</span>
              <span class="pay-row__value" dir="ltr">{{ otaMinus(otaNet.commission) }}</span>
            </div>
            <div v-if="otaNetHasVat" class="pay-row pay-row--net">
              <span class="pay-row__label">{{ $t("otaNet.vat", { vat: otaPct(otaNet.vat_percent) }) }}</span>
              <span class="pay-row__value" dir="ltr">{{ otaMinus(otaNet.vat) }}</span>
            </div>
            <div class="pay-row pay-row--net pay-row--net-result">
              <span class="pay-row__label">
                {{ $t("otaNet.net") }}
                <!-- Botón y no solo tooltip: en el móvil no hay hover y el drawer tapa los
                     tooltips de bootstrap (z-index). El title queda para el ratón. -->
                <button
                  type="button"
                  class="pay-net__info"
                  :class="{ 'pay-net__info--open': otaNetInfoOpen }"
                  :title="otaNetInfoOpen ? null : otaNetInfo"
                  :aria-label="$t('otaNet.howItWorks')"
                  :aria-expanded="otaNetInfoOpen ? 'true' : 'false'"
                  :aria-controls="otaNetInfoId"
                  @click="otaNetInfoOpen = !otaNetInfoOpen"
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                    <circle cx="12" cy="12" r="10"/><path d="M12 16v-4"/><path d="M12 8h.01"/>
                  </svg>
                </button>
              </span>
              <span class="pay-row__value pay-net__amount" dir="ltr">{{ format(otaNet.net) }}</span>
            </div>
            <transition name="pay-net-note">
              <p v-show="otaNetInfoOpen" :id="otaNetInfoId" class="pay-net__note">{{ otaNetInfo }}</p>
            </transition>
            <p v-if="otaNetApart > 0" class="pay-net__hint">
              {{ $t("otaNet.apart", { amount: otaIsolate(format(otaNetApart)), channel: otaNetChannel }) }}
            </p>
          </template>
        </div>
      </template>

      <!-- ══ Pagos (multi-pago por líneas) ══ -->
      <div class="pay-divider"></div>
      <reservation-payments-panel
        v-if="reservationData && reservationData.id"
        :reservation-data="reservationData"
        @changed="onPaymentsChanged"
      />

      <!-- Línea de pendiente cuando hay pago parcial -->
      <transition name="fade-up">
        <div v-if="currentPayStatus === 4 && existingPartialAmount > 0" class="pay-row pay-row--partial-due">
          <span class="pay-row__partial-label">
            <i class="pi pi-clock" style="font-size:11px"></i>
            {{ $t("reservasSmallHc.payData.pendingCharge") }}
          </span>
          <span class="pay-row__partial-value">{{ fmtCode(existingPartialRemaining) }}</span>
        </div>
      </transition>

      <div v-if="hasFinancialBreakdown" class="pay-breakdown-link-row">
        <button class="pay-breakdown-link" @click="showBreakdownModal = true" type="button">
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
            <polyline points="14 2 14 8 20 8"/>
            <line x1="16" y1="13" x2="8" y2="13"/>
            <line x1="16" y1="17" x2="8" y2="17"/>
            <polyline points="10 9 9 9 8 9"/>
          </svg>
          {{ $t("reservasSmallHc.payData.viewBreakdown") }}
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2">
            <polyline points="9 18 15 12 9 6"/>
          </svg>
        </button>
      </div>

      <!-- Recibo (la factura electrónica se gestiona en la pestaña Cargos). -->
      <div v-if="canViewReceipt" class="pi-actions-row mt-4">
        <button class="pi-btn pi-btn--receipt" type="button"
          :disabled="!reservationData?.id" @click="openReceiptModal">
          <span class="pi-btn--receipt__ic"><i class="fa-solid fa-receipt"></i></span>
          <span>{{ $t("reservasSmallHc.payData.viewReceipt") }}</span>
          <i class="fa-solid fa-chevron-right pi-btn--receipt__chev"></i>
        </button>
      </div>

      <div v-if="canViewReceipt" class="pi-hints mt-2">
        <small v-if="canUseInvoicing" class="text-muted">
          {{ $t("reservasSmallHc.payData.receiptInfoPre") }} <strong>{{ $t("reservasSmallHc.payData.chargesTab") }}</strong>.
        </small>
        <small v-else class="text-muted">
          {{ $t("reservasSmallHc.payData.receiptInfoNoInvoice") }}
        </small>
      </div>
    </div>

    <!-- ══ Modal estado pago ══════════════════════════════════════════════ -->
    <transition name="modal-fade">
      <div v-if="isPaymentModalOpen" class="vmodal-overlay" @click.self="closeModal">
        <div class="vmodal">
          <div class="vmodal__header">
            <div class="vmodal__header-left">
              <div class="vmodal__icon"><i class="pi pi-sync"></i></div>
              <div>
                <h5 class="vmodal__title">{{ $t("booking.changePaymentStatusHeader") }}</h5>
                <p class="vmodal__subtitle">{{ $t("reservasSmallHc.payData.reservationNumber", { code: reservationData?.codigo_reserva || reservationData?.id }) }}</p>
              </div>
            </div>
            <button class="vmodal__close" type="button" @click="closeModal"><i class="pi pi-times"></i></button>
          </div>
          <div class="vmodal__body">
            <div v-if="payStatusConfig" class="vmodal__current">
              <span class="vmodal__current-label">{{ $t("reservasSmallHc.payData.currentStatus") }}</span>
              <span class="vmodal__current-badge"
                :style="{ background: payStatusConfig.bg, color: payStatusConfig.color, border: `1px solid ${payStatusConfig.border}` }">
                <i :class="payStatusConfig.icon" style="font-size:11px"></i>
                {{ payStatusConfig.label }}
              </span>
            </div>
            <div class="vmodal__field">
              <label class="vmodal__label">{{ $t("booking.paymentStatusLabel") }}</label>
              <div class="vstatus-select" v-click-outside="() => statusDropdownOpen = false">
                <button
                  class="vstatus-select__trigger"
                  type="button"
                  @click="statusDropdownOpen = !statusDropdownOpen"
                  :class="{ 'vstatus-select__trigger--open': statusDropdownOpen }"
                >
                  <template v-if="selectedStatusOption">
                    <span class="vstatus-select__badge"
                      :style="{ background: selectedStatusOption.bg, color: selectedStatusOption.color, border: `1px solid ${selectedStatusOption.border}` }">
                      <i :class="selectedStatusOption.icon"></i>
                    </span>
                    <span class="vstatus-select__current-label">{{ selectedStatusOption.label }}</span>
                  </template>
                  <span v-else class="vstatus-select__placeholder">{{ $t("booking.select") }}</span>
                  <i class="pi pi-chevron-down vstatus-select__chevron"
                    :style="{ transform: statusDropdownOpen ? 'rotate(180deg)' : 'none' }"></i>
                </button>

                <transition name="vstatus-drop">
                  <div v-if="statusDropdownOpen" class="vstatus-select__menu">
                    <button
                      v-for="opt in statusOptions"
                      :key="opt.value"
                      type="button"
                      class="vstatus-select__option"
                      :class="{ 'vstatus-select__option--selected': paymentStatus === opt.value }"
                      @click="onStatusOptionClick(opt)"
                    >
                      <span class="vstatus-select__opt-icon"
                        :style="{ background: opt.bg, color: opt.color, border: `1px solid ${opt.border}` }">
                        <i :class="opt.icon"></i>
                      </span>
                      <span class="vstatus-select__opt-label">{{ opt.label }}</span>
                      <i v-if="paymentStatus === opt.value" class="pi pi-check vstatus-select__opt-check"></i>
                    </button>
                  </div>
                </transition>
              </div>
            </div>

            <transition name="fade-up">
              <div v-if="selectedStatusOption" class="vmodal__preview">
                <span class="vmodal__preview-arrow">→</span>
                <span style="color:#6b7280;font-size:12px">{{ $t("reservasSmallHc.payData.willChangeTo") }}</span>
                <span class="vmodal__preview-badge"
                  :style="{ background: selectedStatusOption.bg, color: selectedStatusOption.color, border: `1px solid ${selectedStatusOption.border}` }">
                  <i :class="selectedStatusOption.icon" style="font-size:10px"></i>
                  {{ selectedStatusOption.label }}
                </span>
              </div>
            </transition>
          </div>
          <div class="vmodal__footer">
            <button class="vmodal__btn vmodal__btn--cancel" type="button" :disabled="isSavingStatus" @click="closeModal">
              {{ $t("booking.close") }}
            </button>
            <button class="vmodal__btn vmodal__btn--save" type="button"
              :disabled="!selectedStatusOption || isSavingStatus" @click="changePaymentReservation">
              <span v-if="isSavingStatus" class="vmodal__spinner"></span>
              <i v-else class="pi pi-check"></i>
              {{ isSavingStatus ? $t("reservasSmallHc.payData.saving") : $t("booking.saveChanges") }}
            </button>
          </div>
        </div>
      </div>
    </transition>

    <!-- ══ Indicador asíncrono de envío WhatsApp ════════════════════════════ -->
    <transition name="wa-async-slide">
      <div v-if="waAsyncState" class="wa-async-indicator" :class="`wa-async-indicator--${waAsyncState}`">
        <span v-if="waAsyncState === 'sending'" class="wa-async-spinner"></span>
        <i v-else-if="waAsyncState === 'sent'"  class="pi pi-check-circle wa-async-icon"></i>
        <i v-else                               class="pi pi-exclamation-circle wa-async-icon"></i>
        <span class="wa-async-text">{{ waAsyncMessage }}</span>
      </div>
    </transition>

    <ConfirmSendInvoiceLinkModal ref="invoiceModal" :reservaId="reservationData?.id || null" />
    <ManualInvoiceCreateModal
      ref="manualInvoiceModal"
      :reservationData="reservationData"
      :billingProfile="billingProfile"
      @created="handleManualInvoiceCreated"
    />

    <!-- ══ Modal desglose de precios ════════════════════════════════════════ -->
    <transition name="bkd-fade">
      <div v-if="showBreakdownModal" class="bkd-overlay" @click.self="showBreakdownModal = false">
        <div class="bkd-modal">

          <!-- Cabecera -->
          <div class="bkd-header">
            <div class="bkd-header__left">
              <div class="bkd-header__icon">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                  <polyline points="14 2 14 8 20 8"/>
                  <line x1="16" y1="13" x2="8" y2="13"/>
                  <line x1="16" y1="17" x2="8" y2="17"/>
                </svg>
              </div>
              <div>
                <h3 class="bkd-header__title">{{ $t("reservasSmallHc.payData.breakdownTitle") }}</h3>
                <p class="bkd-header__sub">{{ $t("reservasSmallHc.payData.reservationNumber", { code: reservationData?.codigo_reserva || reservationData?.id }) }}</p>
              </div>
            </div>
            <button class="bkd-close" @click="showBreakdownModal = false" type="button">
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
                <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
              </svg>
            </button>
          </div>

          <!-- Cuerpo -->
          <div class="bkd-body">

            <!-- Alojamiento / Noches -->
            <div v-if="breakdownNights.length" class="bkd-section">
              <div class="bkd-section__label">{{ $t("reservasSmallHc.payData.lodging") }}</div>
              <div v-for="(line, i) in breakdownNights" :key="`night-${i}`" class="bkd-line">
                <span class="bkd-line__desc">{{ breakdownNightDesc(line) }}</span>
                <span class="bkd-line__amount">{{ formatBreakdownLineAmount(line) }}</span>
              </div>
            </div>

            <!-- Servicios y extras (líneas del quote + extras del motor/agente/manuales) -->
            <div v-if="breakdownExtras.length || invoiceChargeLines.length" class="bkd-section">
              <div class="bkd-section__label">{{ $t("reservasSmallHc.payData.servicesExtras") }}</div>
              <div v-for="(line, i) in breakdownExtras" :key="`extra-${i}`" class="bkd-line">
                <span class="bkd-line__desc">{{ breakdownNightDesc(line) }}</span>
                <span class="bkd-line__amount">{{ formatBreakdownLineAmount(line) }}</span>
              </div>
              <div v-for="(line, i) in invoiceChargeLines" :key="`chg-${i}`" class="bkd-line">
                <span class="bkd-line__desc">
                  {{ line.description }}<template v-if="line.quantity > 1"> × {{ line.quantity }}</template><template v-if="line.room_name"> · {{ line.room_name }}</template>
                </span>
                <span class="bkd-line__amount">{{ format(line.amount) }}</span>
              </div>
            </div>

            <!-- Descuentos -->
            <div v-if="breakdownDiscounts.length" class="bkd-section">
              <div class="bkd-section__label">{{ $t("reservasSmallHc.payData.discounts") }}</div>
              <div v-for="(line, i) in breakdownDiscounts" :key="`disc-${i}`" class="bkd-line">
                <span class="bkd-line__desc">{{ line.description }}</span>
                <span class="bkd-line__amount bkd-line__amount--discount">{{ formatBreakdownLineAmount(line) }}</span>
              </div>
            </div>

            <!-- Impuestos -->
            <div v-if="breakdownTaxes.length" class="bkd-section">
              <div class="bkd-section__label">{{ $t("reservasSmallHc.payData.taxesAndFees") }}</div>
              <div v-for="(line, i) in breakdownTaxes" :key="`tax-${i}`" class="bkd-line">
                <span class="bkd-line__desc">{{ line.description }}</span>
                <span class="bkd-line__amount">{{ formatBreakdownLineAmount(line) }}</span>
              </div>
            </div>

            <!-- IVA incluido en el precio (informativo, no se suma al total) -->
            <div v-if="reservaIvaRate > 0" class="bkd-included-note">
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/>
                <circle cx="12" cy="16" r="1" fill="currentColor"/>
              </svg>
              {{ $t("reservasSmallHc.payData.ivaIncludedInPrice", { rate: reservaIvaRate, amount: format(reservaIvaIncluded) }) }}
            </div>

            <!-- Incluidos -->
            <div v-if="getIncludedTaxes() > 0" class="bkd-included-note">
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/>
                <circle cx="12" cy="16" r="1" fill="currentColor"/>
              </svg>
              {{ $t("reservasSmallHc.payData.includesTaxes", { amount: format(getIncludedTaxes()) }) }}
            </div>

            <!-- Separador y total -->
            <div class="bkd-divider"></div>

            <div class="bkd-total">
              <span class="bkd-total__label">{{ $t("reservasSmallHc.payData.total") }}</span>
              <span class="bkd-total__amount">{{ getFormattedPrice() }}</span>
            </div>
          </div>

          <!-- Pie -->
          <div class="bkd-footer">
            <button class="bkd-footer__btn" @click="showBreakdownModal = false" type="button">{{ $t("reservasSmallHc.payData.close") }}</button>
          </div>

        </div>
      </div>
    </transition>

    <!-- ══ Modal recibo ═══════════════════════════════════════════════════════ -->
    <transition name="receipt-modal">
      <div v-if="showReceiptModal" class="receipt-overlay" @click.self="closeReceiptModal">
        <div class="receipt-modal-container">

          <div class="receipt-modal-header">
            <div class="receipt-modal-header__left">
              <div class="receipt-modal-header__icon">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8">
                  <path d="M9 5H7a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2h-2"/>
                  <rect x="9" y="3" width="6" height="4" rx="1"/>
                  <line x1="9" y1="12" x2="15" y2="12"/>
                  <line x1="9" y1="16" x2="13" y2="16"/>
                </svg>
              </div>
              <h3>{{ $t("reservasSmallHc.payData.viewReceipt") }}</h3>
            </div>
            <button class="receipt-modal-close" @click="closeReceiptModal" type="button">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
                <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
              </svg>
            </button>
          </div>

          <div class="receipt-modal-body">
            <div v-if="loadingReceipt" class="receipt-loading">
              <div class="receipt-spinner"></div>
              <span>{{ $t("reservasSmallHc.payData.loadingReceipt") }}</span>
            </div>

            <template v-else-if="receiptData">

              <!-- Encabezado: nombre del alojamiento + contacto + importe -->
              <div class="rcpt-top">
                <div class="rcpt-top__head">
                <div class="rcpt-top__company">
                  <div class="rcpt-top__company-icon">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
                      <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/>
                      <polyline points="9 22 9 12 15 12 15 22"/>
                    </svg>
                  </div>
                  <!-- ✅ Solo nombre del listing, sin datos fiscales -->
                  <div class="rcpt-top__company-info">
                    <span class="rcpt-top__company-name">{{ receiptData.issuer.name }}</span>
                    <span v-if="receiptData.issuer.email" class="rcpt-top__company-contact">
                      {{ receiptData.issuer.email }}
                    </span>
                    <span v-if="receiptData.issuer.phone" class="rcpt-top__company-contact">
                      {{ receiptData.issuer.phone }}
                    </span>
                  </div>
                </div>

                  <div class="rcpt-top__amount-block">
                    <div class="rcpt-top__amount">{{ formatMoney(receiptGrandTotal) }}</div>
                    <div class="rcpt-top__sub">
                      {{ $t("reservasSmallHc.payData.stayReceipt", { date: formatReceiptDate(receiptData.reservation.checkin) }) }}
                    </div>
                  </div>
                </div><!-- /.rcpt-top__head -->

                <div class="rcpt-top__actions">
                  <button class="rcpt-action-btn rcpt-action-btn--download" @click="downloadReceipt" type="button">
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2">
                      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                      <polyline points="7 10 12 15 17 10"/>
                      <line x1="12" y1="15" x2="12" y2="3"/>
                    </svg>
                    {{ $t("reservasSmallHc.payData.downloadPdf") }}
                  </button>

                  <button class="rcpt-action-btn"
                    :class="{
                      'rcpt-action-btn--wa':      !waReceiptSent && !waReceiptOutOfWindow,
                      'rcpt-action-btn--wa-sent':  waReceiptSent,
                      'rcpt-action-btn--wa-off':   waReceiptOutOfWindow,
                    }"
                    :disabled="waReceiptSending || waReceiptSent || waReceiptOutOfWindow"
                    @click="sendReceiptWhatsapp" type="button">
                    <template v-if="waReceiptSending">
                      <span class="rcpt-btn-spinner"></span> {{ $t("reservasSmallHc.payData.sending") }}
                    </template>
                    <template v-else-if="waReceiptSent">
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><polyline points="20 6 9 17 4 12"/></svg>
                      {{ $t("reservasSmallHc.payData.sent") }}
                    </template>
                    <template v-else-if="waReceiptOutOfWindow">
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><circle cx="12" cy="16" r="1" fill="currentColor"/></svg>
                      {{ $t("reservasSmallHc.payData.outOfWindow") }}
                    </template>
                    <template v-else>
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"/></svg>
                      {{ $t("reservasSmallHc.payData.sendWhatsapp") }}
                    </template>
                  </button>
                </div>
              </div>

              <!-- Detalle del recibo -->
              <div class="rcpt-card">
                <div class="rcpt-card__header">{{ $t("reservasSmallHc.payData.stayDetail") }}</div>

                <!-- ── Filas por noche reales (de financial_lines) ── -->
                <template v-if="receiptUseNightLines">
                  <div v-for="(nLine, idx) in receiptNightLines" :key="'n_' + idx" class="rcpt-service">
                    <div class="rcpt-service__info">
                      <strong>{{ nLine.description || $t("reservasSmallHc.payData.lodging") }}</strong>
                      <span v-if="nLine.date">{{ nLine.date }}</span>
                      <span v-if="receiptTaxRate > 0">{{ $t("reservasSmallHc.payData.ivaIncludedShort", { rate: receiptTaxRate }) }}</span>
                    </div>
                    <div class="rcpt-service__price">{{ formatMoney(nLine.amount) }}</div>
                  </div>

                  <!-- Descuentos -->
                  <template v-if="receiptDiscountLines && receiptDiscountLines.length">
                    <div v-for="(dLine, idx) in receiptDiscountLines" :key="'d_' + idx" class="rcpt-service rcpt-service--discount">
                      <div class="rcpt-service__info">
                        <strong class="rcpt-discount-name">{{ dLine.description || $t("reservasSmallHc.payData.discount") }}</strong>
                        <span v-if="dLine.date">{{ dLine.date }}</span>
                      </div>
                      <div class="rcpt-service__price rcpt-discount-price">{{ formatMoney(dLine.amount) }}</div>
                    </div>
                  </template>
                </template>

                <!-- ── Fallback: una sola fila promedio ── -->
                <template v-else>
                  <div class="rcpt-service">
                    <div class="rcpt-service__info">
                      <strong>{{ $t("reservasSmallHc.payData.lodging") }}</strong>
                      <span>
                        {{ formatReceiptDate(receiptData.reservation.checkin) }} →
                        {{ formatReceiptDate(receiptData.reservation.checkout) }}
                      </span>
                      <span>
                        {{ formatMoney(receiptPerNight) }} × {{ receiptNights }}
                        {{ $tc("reservasSmallHc.payData.nightsCount", receiptNights) }}
                        <template v-if="receiptTaxRate > 0"> · {{ $t("reservasSmallHc.payData.ivaIncludedShort", { rate: receiptTaxRate }) }}</template>
                      </span>
                    </div>
                    <div class="rcpt-service__price">{{ formatMoney(receiptLodgingPrice) }}</div>
                  </div>
                </template>

                <!-- ── Cargos adicionales ── -->
                <template v-if="receiptHasCharges">
                  <div class="rcpt-hr"></div>
                  <div class="rcpt-card__subheader">{{ $t("reservasSmallHc.payData.additionalCharges") }}</div>
                  <div v-for="(c, idx) in receiptData.charges" :key="idx" class="rcpt-service rcpt-service--charge">
                    <div class="rcpt-service__info">
                      <strong>{{ c.description }}</strong>
                      <span v-if="c.charge_date">{{ c.charge_date }}</span>
                      <span v-if="c.vat_rate > 0">{{ $t("reservasSmallHc.payData.ivaShort", { rate: c.vat_rate }) }}</span>
                    </div>
                    <div class="rcpt-service__price">{{ formatMoney(c.total_with_vat) }}</div>
                  </div>
                </template>

                <div class="rcpt-hr"></div>

                <!-- Resumen totales -->
                <div class="rcpt-row rcpt-row--bold">
                  <span>{{ $t("reservasSmallHc.payData.total") }}</span>
                  <span>{{ formatMoney(receiptGrandTotal) }}</span>
                </div>
                <div class="rcpt-row" style="color:#16a34a;font-weight:700;">
                  <span>{{ $t("reservasSmallHc.payData.charged") }}</span>
                  <span>{{ formatMoney(receiptPaid) }}</span>
                </div>
                <div v-if="receiptPending > 0" class="rcpt-row" style="color:#e65100;font-weight:700;">
                  <span>{{ $t("reservasSmallHc.payData.pending") }}</span>
                  <span>{{ formatMoney(receiptPending) }}</span>
                </div>
              </div>

              <!-- Disclaimer -->
              <div class="rcpt-disclaimer">
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" style="flex-shrink:0;margin-top:1px">
                  <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><circle cx="12" cy="16" r="1" fill="currentColor"/>
                </svg>
                {{ receiptData.disclaimer }}
              </div>
              <div class="rcpt-footer">{{ $t("reservasSmallHc.payData.receiptGeneratedBy") }} <strong>WaCheckin</strong></div>

            </template>
          </div>

          <div class="receipt-modal-footer">
            <button class="rcpt-btn-listo" @click="closeReceiptModal" type="button">{{ $t("reservasSmallHc.payData.done") }}</button>
          </div>

        </div>
      </div>
    </transition>

    <!-- ══ Modal pago parcial ═══════════════════════════════════════════════ -->
    <transition name="partial-fade">
      <div v-if="showPartialModal" class="partial-overlay" @click.self="closePartialModal">
        <div class="partial-modal">

          <!-- Cabecera -->
          <div class="partial-modal__header">
            <div class="partial-modal__header-left">
              <div class="partial-modal__icon">
                <i class="pi pi-wallet"></i>
              </div>
              <div>
                <h5 class="partial-modal__title">{{ $t("reservasSmallHc.payData.registerPartial") }}</h5>
                <p class="partial-modal__sub">{{ $t("reservasSmallHc.payData.reservationNumber", { code: reservationData?.codigo_reserva || reservationData?.id }) }}</p>
              </div>
            </div>
            <button class="partial-modal__close" type="button" @click="closePartialModal">
              <i class="pi pi-times"></i>
            </button>
          </div>

          <!-- Cuerpo -->
          <div class="partial-modal__body">

            <!-- Total -->
            <div class="partial-total-banner">
              <span class="partial-total-banner__label">{{ $t("reservasSmallHc.payData.reservationTotal") }}</span>
              <span class="partial-total-banner__amount">{{ fmtCode(partialTotalPrice) }}</span>
            </div>

            <!-- Input monto abonado -->
            <div class="partial-field">
              <label class="partial-field__label">{{ $t("reservasSmallHc.payData.amountReceived") }}</label>
              <div class="partial-field__input-wrap">
                <span class="partial-field__currency-badge">{{ resolvedCurrency }}</span>
                <input
                  v-model="partialAmount"
                  type="number"
                  step="0.01"
                  min="0.01"
                  class="partial-field__input partial-field__input--with-badge"
                  placeholder="0.00"
                  @input="partialPaymentUrl = null"
                />
              </div>
            </div>

            <!-- Saldo restante en tiempo real -->
            <transition name="fade-up">
              <div v-if="partialAmountParsed > 0" class="partial-balance-row">
                <div class="partial-balance-row__paid">
                  <span class="partial-balance-row__lbl">{{ $t("reservasSmallHc.payData.paid") }}</span>
                  <span class="partial-balance-row__val partial-balance-row__val--paid">{{ fmtCode(partialAmountParsed) }}</span>
                </div>
                <div class="partial-balance-row__sep">→</div>
                <div class="partial-balance-row__due">
                  <span class="partial-balance-row__lbl">{{ $t("reservasSmallHc.payData.pendingBalance") }}</span>
                  <span class="partial-balance-row__val partial-balance-row__val--due">{{ fmtCode(partialRemaining) }}</span>
                </div>
              </div>
            </transition>

            <!-- Alerta si monto inválido -->
            <div v-if="partialAmountParsed >= partialTotalPrice && partialAmountParsed > 0" class="partial-alert">
              <i class="pi pi-exclamation-triangle"></i>
              {{ $t("reservasSmallHc.payData.amountExceedsTotal") }} <strong>{{ $t("reservasSmallHc.payData.paidStatus") }}</strong>.
            </div>

            <!-- Nota interna -->
            <div class="partial-field">
              <label class="partial-field__label">{{ $t("reservasSmallHc.payData.internalNote") }} <span style="font-weight:400;color:#9ca3af">{{ $t("reservasSmallHc.payData.optional") }}</span></label>
              <textarea
                v-model="partialNote"
                class="partial-field__textarea"
                rows="2"
                :placeholder="$t('reservasSmallHc.payData.notePlaceholder')"
              ></textarea>
            </div>

            <!-- Nota automática del link -->
            <div v-if="hasPaymentProvider && partialIsValid" class="partial-auto-link-note">
              <i class="pi pi-info-circle"></i>
              {{ $t("reservasSmallHc.payData.autoLinkNote") }} <strong>{{ fmtCode(partialRemaining) }}</strong>.
            </div>

          </div>

          <!-- Pie -->
          <div class="partial-modal__footer">
            <button class="partial-modal__btn partial-modal__btn--cancel" type="button" :disabled="isSavingPartial" @click="closePartialModal">
              {{ $t("reservasSmallHc.payData.cancel") }}
            </button>
            <button
              class="partial-modal__btn partial-modal__btn--save"
              type="button"
              :disabled="!partialIsValid || isSavingPartial"
              @click="savePartialPayment"
            >
              <span v-if="isSavingPartial" class="partial-spinner partial-spinner--white"></span>
              <i v-else class="pi pi-check"></i>
              {{ isSavingPartial ? $t("reservasSmallHc.payData.saving") : $t("reservasSmallHc.payData.savePartial") }}
            </button>
          </div>

        </div>
      </div>
    </transition>

  </div>
</template>

<style scoped>
.data-guest {
  --brand:       #5e71e4;
  --brand-light: #f0f4ff;
  --brand-hover: #4a5fd4;
  --green:       #4caf50;
  --green-light: #e4f2e4;
  --radius:      12px;
  --radius-btn:  8px;
}

/* ══ Cabecera ═══════════════════════════════════════════════════════════ */
.paydata-header { display:flex; justify-content:space-between; align-items:center; margin-bottom:16px; }

/* ══ Dropdown ═══════════════════════════════════════════════════════════ */
.vdropdown { position:relative; }
.vdropdown__trigger {
  display:flex; flex-direction:column; align-items:center; justify-content:center;
  gap:3.5px; width:32px; height:32px; border:none; background:transparent;
  border-radius:8px; cursor:pointer; padding:0; transition:background .15s;
}
.vdropdown__trigger:hover { background:#f3f4f6; }
.vdropdown__dot { display:block; width:4px; height:4px; border-radius:50%; background:#9ca3af; transition:background .15s; }
.vdropdown__trigger:hover .vdropdown__dot { background:var(--brand); }
.vdropdown__menu {
  position:absolute; right:0; top:calc(100% + 6px); min-width:220px;
  background:#fff; border:1px solid #e5e7eb; border-radius:12px;
  box-shadow:0 8px 24px rgba(0,0,0,.12); z-index:1050; padding:6px;
}
.vdropdown__item {
  display:flex; align-items:center; gap:10px; width:100%; padding:9px 12px;
  border:none; background:transparent; border-radius:8px; font-size:13px;
  font-weight:500; color:#374151; cursor:pointer; text-align:left; transition:background .12s;
}
.vdropdown__item:hover:not(:disabled) { background:var(--brand-light); color:var(--brand); }
.vdropdown__item i { font-size:13px; color:#9ca3af; width:16px; flex-shrink:0; }
.vdropdown__item:disabled { opacity:.55; cursor:wait; }
.vdropdown__info { display:flex; align-items:center; gap:8px; padding:7px 12px; font-size:11px; color:#9ca3af; }
.vdropdown__divider { height:1px; background:#f3f4f6; margin:4px 0; }
.vdropdown__spinner { display:inline-block; width:13px; height:13px; flex-shrink:0; border:2px solid rgba(94,113,228,.2); border-top-color:var(--brand); border-radius:50%; animation:spin .7s linear infinite; }
.vdropdown__progress { height:2px; background:#e5e7eb; border-radius:2px; overflow:hidden; margin:2px 6px 4px; }
.vdropdown__progress-fill { height:100%; width:40%; background:var(--brand); border-radius:2px; animation:progress-slide 1.2s ease-in-out infinite; }
@keyframes progress-slide { 0%{transform:translateX(-150%)} 100%{transform:translateX(350%)} }
.vdrop-enter-active,.vdrop-leave-active{transition:opacity .15s,transform .15s;}
.vdrop-enter-from,.vdrop-leave-to{opacity:0;transform:translateY(-6px) scale(.97);}

/* ══ Totales ═════════════════════════════════════════════════════════════ */
.pay-totals-block { background:#fff; border-radius:var(--radius); }
.pay-group-note {
  display:flex; align-items:flex-start; gap:8px;
  background:#eef0ff; color:#3d3f7a; border:1px solid #e0e4ff;
  border-radius:10px; padding:9px 12px; font-size:12.5px; line-height:1.4; margin-bottom:10px;
}
.pay-group-note i { color:#5e71e4; margin-top:2px; }
.pay-group-note strong { color:#5e71e4; }
.pay-status-badge { display:inline-flex; align-items:center; gap:6px; padding:5px 12px; border-radius:20px; font-size:12px; font-weight:600; margin-bottom:14px; }
.pay-row { display:flex; justify-content:space-between; align-items:center; padding-block:7px; font-size:15px; color:#374151; }
.pay-row__label { color:#6b7280; }
.pay-row__hint { font-size:12px; color:#9ca3af; font-weight:400; margin-left:2px; }
.pay-row__value { font-weight:500; color:#111827; }
.pay-row--total { font-size:16px; font-weight:800; color:#111827; padding-block:10px; }
.pay-divider { height:1px; background:#e5e7eb; margin-block:8px; }

/* ── Servicios y extras desplegable ── */
.pay-row--expandable { cursor:pointer; border-radius:8px; margin-inline:-6px; padding-inline:6px; transition:background .15s; user-select:none; }
.pay-row--expandable:hover { background:#f7f7fb; }
.pay-row--expandable:focus-visible { outline:2px solid var(--brand-light); outline-offset:1px; }
.pay-row--expandable-open .pay-row__label--toggle { color:#374151; font-weight:600; }
.pay-row__label--toggle { display:inline-flex; align-items:center; gap:6px; }
.pay-row__caret { color:#9ca3af; transition:transform .18s ease; flex:none; }
.pay-row__caret--open { transform:rotate(90deg); color:var(--brand); }
.pay-row__count { display:inline-flex; align-items:center; justify-content:center; min-width:16px; height:16px; padding:0 4px; border-radius:8px; background:#eef0ff; color:#5e71e4; font-size:10.5px; font-weight:700; line-height:1; }
.pay-extras-detail { overflow:hidden; padding-left:17px; margin-block:2px 4px; border-left:2px solid #eef0ff; margin-left:5px; }
.pay-extra-line { display:flex; justify-content:space-between; align-items:center; gap:10px; padding-block:4px; font-size:12.5px; }
.pay-extra-line__desc { color:#6b7280; }
.pay-extra-line__amount { color:#374151; font-weight:500; white-space:nowrap; }
.extras-expand-enter-active, .extras-expand-leave-active { transition:opacity .18s ease, max-height .22s ease; max-height:400px; }
.extras-expand-enter-from, .extras-expand-leave-to { opacity:0; max-height:0; }

/* ── Lo que te llega (reserva de OTA) ──
   Mismas filas que el desglose (.pay-row): solo se añade el hueco entre etiqueta e
   importe, para que una etiqueta larga («Comisión y cargos de Booking.com…») parta en
   dos líneas en el móvil sin pegarse a la cifra, que nunca se corta. El título va como
   los rótulos de Pendiente/Cobrado/Total de justo debajo, y el verde es el de «Cobrado». */
.pay-net__title { display:block; margin-bottom:2px; font-size:11px; font-weight:600; letter-spacing:.04em; text-transform:uppercase; color:#8a8f9c; }
.pay-row--net { gap:12px; }
.pay-row--net .pay-row__value { white-space:nowrap; }
.pay-row--net-result .pay-row__label { display:inline-flex; align-items:center; gap:4px; color:#374151; font-weight:600; }
.pay-net__amount { color:#16a34a; font-weight:700; }
.pay-net__info {
  display:inline-flex; align-items:center; justify-content:center; flex:none;
  width:22px; height:22px; padding:0; border:none; border-radius:6px;
  background:transparent; color:#6b7280; cursor:pointer; transition:background .15s, color .15s;
}
.pay-net__info:hover, .pay-net__info--open { background:#f3f4f6; color:#374151; }
.pay-net__info:focus-visible { outline:2px solid var(--brand); outline-offset:1px; }
.pay-net__note {
  display:flex; align-items:flex-start; gap:7px; margin:2px 0 4px; padding:8px 10px;
  border-radius:8px; background:#f7f8fb; font-size:12.5px; line-height:1.45; color:#6b7280;
}
.pay-net__note-ic { flex:none; margin-top:2px; color:#9ca3af; }
/* El aviso de lo cobrado aparte es información, no adorno: gris de las etiquetas (AA). */
.pay-net__hint { margin:2px 0 0; font-size:12px; line-height:1.4; color:#6b7280; }
.pay-net-note-enter-active, .pay-net-note-leave-active { transition:opacity .16s ease, transform .16s ease; }
.pay-net-note-enter, .pay-net-note-leave-to { opacity:0; transform:translateY(-3px); }
@media (prefers-reduced-motion: reduce) {
  .pay-net-note-enter-active, .pay-net-note-leave-active { transition:none; }
}

/* ══ Botones ═════════════════════════════════════════════════════════════ */
.pi-actions-row { display:flex; flex-wrap:wrap; gap:8px; align-items:center; }
.pi-btn { display:inline-flex; align-items:center; gap:7px; padding:8px 14px; border-radius:var(--radius-btn); font-size:13px; font-weight:600; cursor:pointer; border:none; transition:all .15s; white-space:nowrap; }
.pi-btn:disabled { opacity:.5; cursor:not-allowed; }
/* Misma familia de marca que "Ver recibo": secundarios en blanco con borde
   morado claro + texto de marca; el primario (enviar link) relleno de marca. */
.pi-btn--copy    { background:#fff; color:var(--brand); border:1.5px solid var(--brand-light); padding:9px 12px; }
.pi-btn--copy:hover:not(:disabled) { background:var(--brand-light); border-color:var(--brand); }
.pi-btn--invoice { background:#fff; color:var(--brand); border:1.5px solid var(--brand-light); padding:10px 14px; }
.pi-btn--invoice:hover:not(:disabled) { background:var(--brand-light); border-color:var(--brand); transform:translateY(-1px); }
.pi-btn--send    { background:var(--brand); color:#fff; padding:10px 16px; box-shadow:0 4px 14px -6px rgba(94,113,228,.6); }
.pi-btn--send:hover:not(:disabled) { background:var(--brand-hover); transform:translateY(-1px); box-shadow:0 7px 18px -6px rgba(94,113,228,.7); }
.pi-btn--receipt {
  flex:1; justify-content:flex-start; min-width:180px;
  background:#fff; color:var(--brand);
  border:1.5px solid var(--brand-light);
  box-shadow:0 4px 14px -8px rgba(108,76,240,.5);
  padding:11px 14px; font-size:14px;
}
.pi-btn--receipt:hover:not(:disabled) {
  background:var(--brand-light); border-color:var(--brand);
  transform:translateY(-1px); box-shadow:0 7px 18px -8px rgba(108,76,240,.6);
}
.pi-btn--receipt__ic { width:26px; height:26px; border-radius:8px; background:var(--brand-light); color:var(--brand); display:inline-flex; align-items:center; justify-content:center; font-size:13px; flex-shrink:0; }
.pi-btn--receipt:hover:not(:disabled) .pi-btn--receipt__ic { background:#fff; }
.pi-btn--receipt__chev { margin-left:auto; font-size:11px; opacity:.55; }
.pi-spinner,.vmodal__spinner { display:inline-block; width:13px; height:13px; border:2px solid rgba(0,0,0,.12); border-top-color:currentColor; border-radius:50%; animation:spin .7s linear infinite; }
.vmodal__spinner { width:14px; height:14px; border-color:rgba(255,255,255,.3); border-top-color:#fff; }
@keyframes spin { to{transform:rotate(360deg)} }
.pi-hints { display:flex; flex-direction:column; gap:2px; }

/* ══ Modal estado pago ══════════════════════════════════════════════════ */
.vmodal-overlay { position:fixed; inset:0; background:rgba(0,0,0,.45); backdrop-filter:blur(3px); display:flex; align-items:center; justify-content:center; z-index:9998; padding:20px; }
.vmodal { background:#fff; border-radius:16px; width:100%; max-width:440px; box-shadow:0 20px 60px rgba(0,0,0,.2); overflow:visible; }
.vmodal__header { display:flex; align-items:center; justify-content:space-between; padding:22px 24px 18px; border-bottom:1px solid #f0f0f0; border-radius:16px 16px 0 0; }
.vmodal__header-left { display:flex; align-items:center; gap:14px; }
.vmodal__icon { width:42px; height:42px; background:var(--brand-light); color:var(--brand); border-radius:10px; display:flex; align-items:center; justify-content:center; font-size:18px; flex-shrink:0; }
.vmodal__title { margin:0; font-size:15px; font-weight:700; color:#111827; line-height:1.2; }
.vmodal__subtitle { margin:2px 0 0; font-size:11px; color:#9ca3af; }
.vmodal__close { width:34px; height:34px; border:none; background:#f3f4f6; border-radius:8px; display:flex; align-items:center; justify-content:center; color:#6b7280; cursor:pointer; font-size:13px; transition:all .2s; }
.vmodal__close:hover { background:#ef4444; color:#fff; }
.vmodal__body { padding:24px; display:flex; flex-direction:column; gap:18px; }
.vmodal__current { display:flex; align-items:center; justify-content:space-between; padding:12px 14px; background:#fafafa; border:1px solid #f0f0f0; border-radius:10px; }
.vmodal__current-label { font-size:12px; color:#9ca3af; font-weight:500; }
.vmodal__current-badge { display:inline-flex; align-items:center; gap:5px; padding:4px 10px; border-radius:20px; font-size:11px; font-weight:600; }
.vmodal__field { display:flex; flex-direction:column; gap:8px; }
.vmodal__label { font-size:11px; font-weight:700; color:#374151; letter-spacing:.06em; text-transform:uppercase; }
.vmodal__footer { display:flex; align-items:center; justify-content:flex-end; gap:10px; padding:18px 24px; border-top:1px solid #f0f0f0; background:#fafafa; border-radius:0 0 16px 16px; }
.vmodal__btn { display:inline-flex; align-items:center; gap:7px; padding:10px 20px; border-radius:var(--radius-btn); font-size:14px; font-weight:600; cursor:pointer; border:none; transition:all .15s; }
.vmodal__btn:disabled { opacity:.5; cursor:not-allowed; }
.vmodal__btn--cancel { background:#f3f4f6; color:#374151; }
.vmodal__btn--cancel:hover { background:#e5e7eb; }
.vmodal__btn--save { background:var(--brand); color:#fff; min-width:140px; justify-content:center; }
.vmodal__btn--save:hover:not(:disabled) { background:var(--brand-hover); }
.modal-fade-enter-active,.modal-fade-leave-active{transition:opacity .2s;}
.modal-fade-enter-from,.modal-fade-leave-to{opacity:0;}
.modal-fade-enter-active .vmodal{animation:modal-slide-in .25s ease;}
.modal-fade-leave-active .vmodal{animation:modal-slide-out .2s ease;}
@keyframes modal-slide-in{from{transform:translateY(-16px) scale(.97);opacity:0}to{transform:none;opacity:1}}
@keyframes modal-slide-out{from{transform:none;opacity:1}to{transform:translateY(-16px) scale(.97);opacity:0}}
.fade-up-enter-active,.fade-up-leave-active{transition:opacity .2s,transform .2s;}
.fade-up-enter-from,.fade-up-leave-to{opacity:0;transform:translateY(-4px);}

/* ══ Modal recibo ════════════════════════════════════════════════════════ */
.receipt-overlay { position:fixed; inset:0; background:rgba(0,0,0,.5); backdrop-filter:blur(4px); display:flex; align-items:center; justify-content:center; z-index:9999; padding:20px; }
.receipt-modal-container { background:#fff; border-radius:16px; width:100%; max-width:560px; max-height:90vh; overflow:hidden; display:flex; flex-direction:column; box-shadow:0 20px 60px rgba(0,0,0,.25); }
.receipt-modal-header { padding:20px 24px; border-bottom:1px solid #e5e7eb; display:flex; justify-content:space-between; align-items:center; flex-shrink:0; }
.receipt-modal-header__left { display:flex; align-items:center; gap:12px; }
.receipt-modal-header__icon { width:36px; height:36px; background:var(--brand-light); color:var(--brand); border-radius:10px; display:flex; align-items:center; justify-content:center; }
.receipt-modal-header h3 { margin:0; font-size:1.2rem; font-weight:700; color:#1a1a1a; }
.receipt-modal-close { background:#f3f4f6; border:none; width:34px; height:34px; border-radius:8px; cursor:pointer; display:flex; align-items:center; justify-content:center; color:#6b7280; transition:all .2s; }
.receipt-modal-close:hover { background:#ef4444; color:#fff; }
.receipt-modal-body { padding:24px; overflow-y:auto; flex:1; }
.receipt-modal-footer { padding:16px 24px; border-top:1px solid #e5e7eb; display:flex; justify-content:flex-end; background:#fafafa; flex-shrink:0; }
.rcpt-btn-listo { background:var(--brand); color:#fff; border:none; padding:10px 28px; border-radius:8px; font-weight:600; font-size:.9rem; cursor:pointer; transition:background .2s; }
.rcpt-btn-listo:hover { background:var(--brand-hover); }
.receipt-loading { display:flex; flex-direction:column; align-items:center; justify-content:center; gap:14px; padding:60px 20px; color:#6b7280; font-size:14px; }
.receipt-spinner { width:28px; height:28px; border:3px solid #e5e7eb; border-top-color:var(--brand); border-radius:50%; animation:spin .8s linear infinite; }

/* Encabezado del recibo */
.rcpt-top { margin-bottom:20px; padding-bottom:20px; border-bottom:1px solid #f3f4f6; }
.rcpt-top__company { display:flex; align-items:flex-start; gap:10px; margin-bottom:12px; }
.rcpt-top__company-icon { width:36px; height:36px; background:var(--brand-light); color:var(--brand); border-radius:10px; display:flex; align-items:center; justify-content:center; flex-shrink:0; margin-top:2px; }
.rcpt-top__company-info { display:flex; flex-direction:column; gap:2px; }
/* ✅ Solo nombre + email + teléfono. Sin NIF ni dirección fiscal */
.rcpt-top__company-name    { font-size:15px; font-weight:700; color:#111827; }
.rcpt-top__company-contact { font-size:12px; color:#6b7280; }
.rcpt-top__amount { font-size:38px; font-weight:700; color:#111827; letter-spacing:-1px; margin-bottom:4px; line-height:1; }
.rcpt-top__sub    { font-size:12px; color:#9ca3af; margin-bottom:16px; }

/* Cabecera del recibo en una fila: alojamiento (izq.) + importe (der.) */
.rcpt-top__head { display:flex; justify-content:space-between; align-items:flex-start; gap:16px; margin-bottom:8px; }
.rcpt-top__head .rcpt-top__company { margin-bottom:0; }
.rcpt-top__amount-block { text-align:right; flex-shrink:0; }
.rcpt-top__amount-block .rcpt-top__amount { font-size:30px; margin-bottom:2px; white-space:nowrap; }
.rcpt-top__amount-block .rcpt-top__sub { margin-bottom:0; }
.rcpt-top__actions { display:flex; gap:10px; flex-wrap:wrap; }

.rcpt-action-btn { display:inline-flex; align-items:center; gap:7px; padding:9px 16px; border-radius:8px; font-size:13px; font-weight:600; cursor:pointer; border:none; transition:all .15s; flex:1; justify-content:center; min-width:150px; }
.rcpt-action-btn:disabled { opacity:.55; cursor:not-allowed; }
.rcpt-action-btn--download { background:#f3f4f6; color:#374151; }
.rcpt-action-btn--download:hover:not(:disabled) { background:#e5e7eb; }
.rcpt-action-btn--wa     { background:#25d366; color:#fff; }
.rcpt-action-btn--wa:hover:not(:disabled) { background:#1ebe5d; }
.rcpt-action-btn--wa-sent { background:#dcfce7; color:#16a34a; }
.rcpt-action-btn--wa-off  { background:#f3f4f6; color:#9ca3af; }
.rcpt-btn-spinner { display:inline-block; width:13px; height:13px; border:2px solid rgba(255,255,255,.3); border-top-color:currentColor; border-radius:50%; animation:spin .7s linear infinite; }

/* Tarjeta de detalle */
.rcpt-card { border:1px solid #e5e7eb; border-radius:12px; overflow:hidden; margin-bottom:16px; }
.rcpt-card__header    { background:#f9fafb; border-bottom:1px solid #e5e7eb; padding:10px 18px; font-size:11px; font-weight:700; color:#374151; text-transform:uppercase; letter-spacing:.06em; }
.rcpt-card__subheader { background:#f9fafb; border-bottom:1px solid #e5e7eb; padding:8px 18px; font-size:11px; font-weight:600; color:#6b7280; }

.rcpt-service { display:flex; justify-content:space-between; align-items:flex-start; padding:14px 18px; gap:12px; }
.rcpt-service--charge { padding:10px 18px; }
.rcpt-service--discount { padding:10px 18px; background:#f0fdf4; border-radius:6px; margin:2px 8px; }
.rcpt-service__info { display:flex; flex-direction:column; gap:3px; }
.rcpt-service__info strong { font-size:13px; font-weight:600; color:#111827; }
.rcpt-service__info span   { font-size:11px; color:#6b7280; }
.rcpt-service__price { font-size:13px; font-weight:600; color:#111827; white-space:nowrap; }
.rcpt-discount-name  { color:#16a34a !important; }
.rcpt-discount-price { color:#16a34a !important; }
.rcpt-hr { height:1px; background:#f3f4f6; }
.rcpt-row { display:flex; justify-content:space-between; align-items:center; padding:10px 18px; font-size:13px; color:#374151; }
.rcpt-row--bold { font-size:14px; font-weight:700; color:#111827; padding-block:13px; }

.rcpt-disclaimer { background:#fffbeb; border:1px solid #fde68a; border-radius:8px; padding:10px 14px; font-size:11px; color:#92400e; line-height:1.6; display:flex; gap:8px; align-items:flex-start; margin-bottom:14px; }
.rcpt-footer { text-align:center; font-size:11px; color:#d1d5db; border-top:1px solid #f3f4f6; padding-top:12px; }

.receipt-modal-enter-active,.receipt-modal-leave-active{transition:opacity .25s;}
.receipt-modal-enter-from,.receipt-modal-leave-to{opacity:0;}
.receipt-modal-enter-active .receipt-modal-container{animation:modal-slide-in .3s ease;}
.receipt-modal-leave-active .receipt-modal-container{animation:modal-slide-out .25s ease;}

.pay-row--muted .pay-row__label,
.pay-row--muted .pay-row__value { color:#94a3b8; }

.pay-row--charge .pay-row__label { color:#475569; }
.pay-row__qty { color:#94a3b8; font-size:11px; margin-left:4px; }

.pay-row--partial-due {
  background: #fff3e0;
  border-radius: 8px;
  padding: 8px 12px;
  margin-top: 4px;
  font-size: 13px;
}
.pay-row__partial-label {
  display: flex; align-items: center; gap: 5px;
  color: #e65100; font-weight: 600;
}
.pay-row__partial-value { font-weight: 800; color: #e65100; font-size: 14px; }

/* ══ Link "Ver desglose" ════════════════════════════════════════════════ */
.pay-breakdown-link-row { margin-top:12px; }
.pay-breakdown-link {
  display:inline-flex; align-items:center; gap:6px;
  background:none; border:none; padding:0; cursor:pointer;
  font-family:'DM Sans', sans-serif; font-size:12.5px; font-weight:600;
  color:var(--brand); letter-spacing:-0.01em; transition:opacity .15s;
  text-decoration:underline; text-decoration-style:dotted; text-underline-offset:3px;
}
.pay-breakdown-link:hover { opacity:.75; }

/* ══ Modal desglose ════════════════════════════════════════════════════ */
.bkd-overlay {
  position:fixed; inset:0; background:rgba(0,0,0,.48); backdrop-filter:blur(4px);
  display:flex; align-items:center; justify-content:center; z-index:10000; padding:20px;
}
.bkd-modal {
  background:#fff; border-radius:20px; width:100%; max-width:480px;
  max-height:88vh; overflow:hidden; display:flex; flex-direction:column;
  box-shadow:0 24px 64px rgba(0,0,0,.22);
  font-family:'DM Sans', sans-serif;
}

/* Header */
.bkd-header {
  display:flex; align-items:center; justify-content:space-between;
  padding:22px 24px 18px; border-bottom:1px solid #f0f0f2; flex-shrink:0;
}
.bkd-header__left { display:flex; align-items:center; gap:14px; }
.bkd-header__icon {
  width:42px; height:42px; background:var(--brand-light); color:var(--brand);
  border-radius:12px; display:flex; align-items:center; justify-content:center; flex-shrink:0;
}
.bkd-header__title {
  margin:0; font-family:'DM Sans',sans-serif; font-size:16px; font-weight:700;
  color:#111827; letter-spacing:-0.025em;
}
.bkd-header__sub { margin:3px 0 0; font-size:11.5px; color:#9ca3af; font-weight:500; }
.bkd-close {
  width:34px; height:34px; border:none; background:#f3f4f6; border-radius:8px;
  display:flex; align-items:center; justify-content:center; color:#6b7280;
  cursor:pointer; transition:all .18s; flex-shrink:0;
}
.bkd-close:hover { background:#ef4444; color:#fff; }

/* Body */
.bkd-body { padding:22px 24px; overflow-y:auto; flex:1; display:flex; flex-direction:column; gap:18px; }

/* Sección */
.bkd-section { display:flex; flex-direction:column; gap:0; }
.bkd-section__label {
  font-family:'DM Sans',sans-serif; font-size:10px; font-weight:700;
  color:#94a3b8; text-transform:uppercase; letter-spacing:.07em;
  margin-bottom:8px; padding-bottom:6px; border-bottom:1px solid #f3f4f6;
}
.bkd-line {
  display:flex; justify-content:space-between; align-items:center;
  padding:7px 0; border-bottom:1px solid #f9fafb;
}
.bkd-line:last-child { border-bottom:none; }
.bkd-line__desc { font-size:13px; color:#374151; font-weight:500; line-height:1.4; padding-right:12px; }
.bkd-line__amount { font-size:13px; font-weight:700; color:#111827; white-space:nowrap; }
.bkd-line__amount--discount { color:#16a34a; }

/* Nota incluidos */
.bkd-included-note {
  display:flex; align-items:center; gap:7px;
  padding:9px 13px; background:#f0f9ff; border:1px solid #bae6fd;
  border-radius:8px; font-size:11.5px; color:#0369a1; font-weight:500; line-height:1.45;
}

/* Total */
.bkd-divider { height:1px; background:#e5e7eb; }
.bkd-total {
  display:flex; justify-content:space-between; align-items:center;
  padding:4px 0 2px;
}
.bkd-total__label {
  font-family:'DM Sans',sans-serif; font-size:15px; font-weight:700; color:#374151;
}
.bkd-total__amount {
  font-family:'DM Sans',sans-serif; font-size:22px; font-weight:800;
  color:#111827; letter-spacing:-0.04em;
}

/* Footer */
.bkd-footer {
  padding:16px 24px; border-top:1px solid #f0f0f2;
  display:flex; justify-content:flex-end; background:#fafafa; flex-shrink:0;
}
.bkd-footer__btn {
  background:var(--brand); color:#fff; border:none;
  padding:10px 28px; border-radius:9px;
  font-family:'DM Sans',sans-serif; font-weight:700; font-size:13.5px;
  cursor:pointer; transition:background .18s;
}
.bkd-footer__btn:hover { background:var(--brand-hover); }

/* Animación */
.bkd-fade-enter-active,.bkd-fade-leave-active { transition:opacity .2s; }
.bkd-fade-enter-from,.bkd-fade-leave-to { opacity:0; }
.bkd-fade-enter-active .bkd-modal { animation:bkd-slide-in .25s ease; }
.bkd-fade-leave-active .bkd-modal { animation:bkd-slide-out .2s ease; }
@keyframes bkd-slide-in { from{transform:translateY(-14px) scale(.97);opacity:0} to{transform:none;opacity:1} }
@keyframes bkd-slide-out { from{transform:none;opacity:1} to{transform:translateY(-14px) scale(.97);opacity:0} }

/* ══ Responsive ══════════════════════════════════════════════════════════ */
@media (max-width:600px) {
  .pi-actions-row{flex-direction:column;align-items:stretch;}
  .pi-btn{width:100%;justify-content:center;}
  .rcpt-top__actions{flex-direction:column;}
  .rcpt-action-btn{min-width:unset;flex:unset;width:100%;}
  .vmodal__footer{flex-direction:column-reverse;}
  .vmodal__btn{width:100%;justify-content:center;}
}

/* ══ Custom status select ════════════════════════════════════════════════ */
.vstatus-select { position: relative; }

.vstatus-select__trigger {
  width: 100%; height: 50px;
  padding: 0 14px;
  border: 1.5px solid #e5e7eb; border-radius: 10px;
  background: #fff;
  display: flex; align-items: center; gap: 10px;
  cursor: pointer; font-size: 14px; font-weight: 500; color: #111827;
  transition: border-color .2s, box-shadow .2s;
  box-sizing: border-box;
}
.vstatus-select__trigger:hover,
.vstatus-select__trigger--open {
  border-color: var(--brand);
  box-shadow: 0 0 0 3px rgba(94,113,228,.10);
}
.vstatus-select__badge {
  width: 30px; height: 30px; border-radius: 8px;
  display: flex; align-items: center; justify-content: center;
  font-size: 13px; flex-shrink: 0;
}
.vstatus-select__current-label { flex: 1; text-align: left; font-weight: 600; }
.vstatus-select__placeholder    { flex: 1; text-align: left; color: #9ca3af; font-weight: 400; }
.vstatus-select__chevron {
  font-size: 11px; color: #9ca3af; flex-shrink: 0;
  transition: transform .2s;
}

.vstatus-select__menu {
  position: absolute; left: 0; right: 0; top: calc(100% + 7px);
  background: #fff; border: 1px solid #e5e7eb; border-radius: 14px;
  box-shadow: 0 12px 40px rgba(0,0,0,.13);
  z-index: 9999; padding: 6px;
  max-height: 280px; overflow-y: auto;
}
.vstatus-select__option {
  width: 100%; padding: 9px 10px;
  display: flex; align-items: center; gap: 11px;
  border: none; background: transparent; border-radius: 9px;
  cursor: pointer; font-size: 13.5px; font-weight: 500;
  color: #374151; text-align: left; transition: background .12s;
}
.vstatus-select__option:hover           { background: #f3f4f6; }
.vstatus-select__option--selected       { background: var(--brand-light); }
.vstatus-select__opt-icon {
  width: 34px; height: 34px; border-radius: 9px; flex-shrink: 0;
  display: flex; align-items: center; justify-content: center;
  font-size: 15px;
}
.vstatus-select__opt-label  { flex: 1; }
.vstatus-select__opt-check  { font-size: 11px; color: var(--brand); font-weight: 700; }

.vstatus-drop-enter-active, .vstatus-drop-leave-active { transition: opacity .15s, transform .15s; }
.vstatus-drop-enter-from, .vstatus-drop-leave-to { opacity: 0; transform: translateY(-6px) scale(.97); }

/* Preview badge update */
.vmodal__preview      { display:flex; align-items:center; gap:8px; padding:10px 14px; background:var(--brand-light); border-radius:8px; font-size:12px; }
.vmodal__preview-arrow { color: var(--brand); font-size: 14px; font-weight: 700; }
.vmodal__preview-badge { display:inline-flex; align-items:center; gap:5px; padding:4px 11px; border-radius:20px; font-size:11px; font-weight:700; margin-left:auto; }

/* ══ Dropdown item parcial ══════════════════════════════════════════════ */
.vdropdown__item--partial i { color:#e65100; }
.vdropdown__item--partial:hover:not(:disabled) { background:#fff3e0; color:#e65100; }

/* ══ Partial info card (in totals) ══════════════════════════════════════ */
.partial-card {
  background: linear-gradient(135deg, #fff8f0 0%, #fff3e0 100%);
  border-radius: 10px;
  padding: 12px 14px;
  margin-bottom: 14px;
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.partial-card__row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 13px;
}
.partial-card__label {
  color: #6b7280;
  display: flex;
  align-items: center;
  gap: 5px;
}
.partial-card__value { font-weight: 700; }
.partial-card__value--paid { color: #4caf50; }
.partial-card__value--due  { color: #e65100; }
.partial-card__note {
  font-size: 11.5px;
  color: #6b7280;
  display: flex;
  align-items: center;
  gap: 5px;
  padding-top: 4px;
  border-top: 1px dashed #fed7aa;
}
.partial-card__link-row {
  display: flex;
  gap: 8px;
  padding-top: 4px;
}
.partial-link-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  border-radius: 7px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  border: none;
  transition: all .15s;
  flex: 1;
  justify-content: center;
  background: #f3f4f6;
  color: #374151;
}
.partial-link-btn:hover { background: #e5e7eb; }
.partial-link-btn--wa { background: #25d366; color: #fff; }
.partial-link-btn--wa:hover { background: #1ebe5d; }
.partial-card__edit-btn {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  background: none;
  border: none;
  padding: 0;
  margin-top: 2px;
  cursor: pointer;
  font-size: 11.5px;
  font-weight: 600;
  color: #e65100;
  letter-spacing: -.01em;
  transition: opacity .15s;
  text-decoration: underline;
  text-decoration-style: dotted;
  text-underline-offset: 3px;
}
.partial-card__edit-btn:hover { opacity: .7; }

/* ══ Modal pago parcial ══════════════════════════════════════════════════ */
.partial-overlay {
  position: fixed; inset: 0;
  background: rgba(0,0,0,.48);
  backdrop-filter: blur(4px);
  display: flex; align-items: center; justify-content: center;
  z-index: 9999; padding: 20px;
}
.partial-modal {
  background: #fff;
  border-radius: 18px;
  width: 100%;
  max-width: 460px;
  box-shadow: 0 24px 64px rgba(0,0,0,.22);
  overflow: hidden;
  display: flex;
  flex-direction: column;
  font-family: 'DM Sans', sans-serif;
}
.partial-modal__header {
  display: flex; align-items: center; justify-content: space-between;
  padding: 22px 24px 18px;
  border-bottom: 1px solid #f0f0f2;
}
.partial-modal__header-left { display: flex; align-items: center; gap: 14px; }
.partial-modal__icon {
  width: 44px; height: 44px;
  background: linear-gradient(135deg, #fff3e0, #ffe0b2);
  color: #e65100;
  border-radius: 12px;
  display: flex; align-items: center; justify-content: center;
  font-size: 18px; flex-shrink: 0;
}
.partial-modal__title { margin: 0; font-size: 15px; font-weight: 700; color: #111827; }
.partial-modal__sub   { margin: 2px 0 0; font-size: 11px; color: #9ca3af; }
.partial-modal__close {
  width: 34px; height: 34px; border: none; background: #f3f4f6;
  border-radius: 8px; display: flex; align-items: center; justify-content: center;
  color: #6b7280; cursor: pointer; font-size: 13px; transition: all .2s;
}
.partial-modal__close:hover { background: #ef4444; color: #fff; }
.partial-modal__body {
  padding: 24px;
  display: flex; flex-direction: column; gap: 18px;
  overflow-y: auto; max-height: 70vh;
}
.partial-modal__footer {
  display: flex; align-items: center; justify-content: flex-end; gap: 10px;
  padding: 18px 24px;
  border-top: 1px solid #f0f0f2;
  background: #fafafa;
}
.partial-modal__btn {
  display: inline-flex; align-items: center; gap: 7px;
  padding: 10px 20px; border-radius: 9px; font-size: 14px; font-weight: 600;
  cursor: pointer; border: none; transition: all .15s;
}
.partial-modal__btn:disabled { opacity: .5; cursor: not-allowed; }
.partial-modal__btn--cancel { background: #f3f4f6; color: #374151; }
.partial-modal__btn--cancel:hover:not(:disabled) { background: #e5e7eb; }
.partial-modal__btn--save {
  background: var(--brand);
  color: #fff;
  min-width: 170px;
  justify-content: center;
}
.partial-modal__btn--save:hover:not(:disabled) { background: var(--brand-hover); }

/* Total banner */
.partial-total-banner {
  display: flex; justify-content: space-between; align-items: center;
  background: #f9fafb; border: 1px solid #e5e7eb;
  border-radius: 10px; padding: 12px 16px;
}
.partial-total-banner__label { font-size: 12px; color: #9ca3af; font-weight: 600; text-transform: uppercase; letter-spacing: .06em; }
.partial-total-banner__amount { font-size: 20px; font-weight: 800; color: #111827; letter-spacing: -.03em; }

/* Field */
.partial-field { display: flex; flex-direction: column; gap: 7px; }
.partial-field__label {
  font-size: 11px; font-weight: 700; color: #374151;
  letter-spacing: .06em; text-transform: uppercase;
}
.partial-field__input-wrap {
  display: flex; align-items: stretch;
  border: 1.5px solid #e5e7eb; border-radius: 10px;
  overflow: hidden; transition: border-color .2s, box-shadow .2s;
  background: #fff;
}
.partial-field__input-wrap:focus-within {
  border-color: #e65100;
  box-shadow: 0 0 0 3px rgba(230,81,0,.1);
}
.partial-field__currency-badge {
  display: flex; align-items: center; justify-content: center;
  padding: 0 14px;
  background: #f3f4f6;
  border-right: 1.5px solid #e5e7eb;
  font-size: 12px; font-weight: 700; color: #6b7280;
  white-space: nowrap; letter-spacing: .04em; flex-shrink: 0;
}
.partial-field__input {
  flex: 1; height: 48px;
  padding: 0 14px;
  border: none; outline: none;
  font-size: 18px; font-weight: 700; color: #111827;
  background: transparent;
  box-sizing: border-box;
}
.partial-field__input--with-badge { padding-left: 14px; }
.partial-field__textarea {
  width: 100%; padding: 10px 13px;
  border: 1.5px solid #e5e7eb; border-radius: 10px;
  font-size: 13px; color: #374151; background: #fff;
  outline: none; resize: none; transition: border-color .2s, box-shadow .2s;
  box-sizing: border-box; font-family: inherit; line-height: 1.5;
}
.partial-field__textarea:focus {
  border-color: #e65100;
  box-shadow: 0 0 0 3px rgba(230,81,0,.1);
}

/* Balance row */
.partial-balance-row {
  display: flex; align-items: center; gap: 12px;
  background: #f9fafb; border: 1px solid #e5e7eb;
  border-radius: 10px; padding: 12px 16px;
}
.partial-balance-row__paid,
.partial-balance-row__due { display: flex; flex-direction: column; gap: 2px; flex: 1; }
.partial-balance-row__sep { color: #d1d5db; font-size: 18px; flex-shrink: 0; }
.partial-balance-row__lbl { font-size: 10px; font-weight: 700; color: #9ca3af; text-transform: uppercase; letter-spacing: .06em; }
.partial-balance-row__val { font-size: 16px; font-weight: 800; letter-spacing: -.03em; }
.partial-balance-row__val--paid { color: #4caf50; }
.partial-balance-row__val--due  { color: #e65100; }

/* Alert */
.partial-alert {
  display: flex; align-items: center; gap: 8px;
  background: #fef3c7; border: 1px solid #fde68a;
  border-radius: 8px; padding: 10px 14px;
  font-size: 12.5px; color: #92400e; font-weight: 500;
}
.partial-alert i { font-size: 14px; color: #f59e0b; flex-shrink: 0; }

/* Auto-link note */
.partial-auto-link-note {
  display: flex; align-items: flex-start; gap: 8px;
  background: #eef2ff; border: 1px solid #c7d2fe;
  border-radius: 9px; padding: 11px 14px;
  font-size: 12.5px; color: #4338ca; line-height: 1.5;
}
.partial-auto-link-note i { font-size: 14px; flex-shrink: 0; margin-top: 1px; }
.partial-auto-link-note strong { font-weight: 700; }

/* Link section */
.partial-link-section { display: flex; flex-direction: column; gap: 12px; }
.partial-generate-btn {
  display: flex; align-items: center; justify-content: center; gap: 8px;
  width: 100%; padding: 12px 16px;
  background: var(--brand); color: #fff;
  border: none; border-radius: 10px;
  font-size: 13.5px; font-weight: 700; cursor: pointer; transition: all .15s;
}
.partial-generate-btn:hover:not(:disabled) { background: var(--brand-hover); }
.partial-generate-btn:disabled { opacity: .5; cursor: not-allowed; }

.partial-link-result {
  display: flex; align-items: flex-start; gap: 12px;
  background: #f0fdf4; border: 1px solid #bbf7d0;
  border-radius: 10px; padding: 12px 14px;
}
.partial-link-result__icon { font-size: 18px; flex-shrink: 0; margin-top: 1px; }
.partial-link-result__content { flex: 1; min-width: 0; }
.partial-link-result__label { margin: 0 0 3px; font-size: 11px; font-weight: 700; color: #16a34a; text-transform: uppercase; letter-spacing: .06em; }
.partial-link-result__url { margin: 0; font-size: 11.5px; color: #374151; word-break: break-all; line-height: 1.4; }
.partial-link-result__actions { display: flex; flex-direction: column; gap: 6px; flex-shrink: 0; }
.partial-link-action {
  width: 32px; height: 32px;
  display: flex; align-items: center; justify-content: center;
  border: none; border-radius: 8px;
  background: #dcfce7; color: #16a34a;
  cursor: pointer; transition: all .15s; font-size: 12px;
}
.partial-link-action:hover { background: #bbf7d0; }
.partial-link-action--wa { background: #25d366; color: #fff; }
.partial-link-action--wa:hover { background: #1ebe5d; }

/* Spinner */
.partial-spinner {
  display: inline-block; width: 13px; height: 13px; flex-shrink: 0;
  border: 2px solid rgba(255,255,255,.3);
  border-top-color: #fff;
  border-radius: 50%; animation: spin .7s linear infinite;
}
.partial-spinner--white {
  border-color: rgba(255,255,255,.3);
  border-top-color: #fff;
}

/* Transition */
.partial-fade-enter-active, .partial-fade-leave-active { transition: opacity .22s; }
.partial-fade-enter-from, .partial-fade-leave-to { opacity: 0; }
.partial-fade-enter-active .partial-modal { animation: partial-slide-in .28s ease; }
.partial-fade-leave-active .partial-modal { animation: partial-slide-out .22s ease; }
@keyframes partial-slide-in  { from{transform:translateY(-16px) scale(.97);opacity:0} to{transform:none;opacity:1} }
@keyframes partial-slide-out { from{transform:none;opacity:1} to{transform:translateY(-16px) scale(.97);opacity:0} }

@media (max-width:600px) {
  .partial-balance-row { flex-direction: column; gap: 8px; }
  .partial-balance-row__sep { display: none; }
  .partial-link-result { flex-wrap: wrap; }
  .partial-link-result__actions { flex-direction: row; }
  .partial-modal__footer { flex-direction: column-reverse; }
  .partial-modal__btn { width: 100%; justify-content: center; }
}

/* ══ Indicador asíncrono WhatsApp (bottom-right) ══════════════════════════ */
.wa-async-indicator {
  position: fixed;
  bottom: 28px;
  right: 28px;
  z-index: 99999;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 13px 18px;
  border-radius: 14px;
  font-size: 13px;
  font-weight: 600;
  box-shadow: 0 8px 32px rgba(0,0,0,.18);
  max-width: 320px;
  line-height: 1.4;
  pointer-events: none;
}
.wa-async-indicator--sending {
  background: #1a1a2e;
  color: #fff;
  border: 1px solid rgba(255,255,255,.08);
}
.wa-async-indicator--sent {
  background: #f0fdf4;
  color: #166534;
  border: 1px solid #bbf7d0;
}
.wa-async-indicator--error {
  background: #fff7ed;
  color: #9a3412;
  border: 1px solid #fed7aa;
}
.wa-async-spinner {
  display: inline-block;
  width: 14px; height: 14px;
  border: 2px solid rgba(255,255,255,.25);
  border-top-color: #fff;
  border-radius: 50%;
  animation: spin .7s linear infinite;
  flex-shrink: 0;
}
.wa-async-icon { font-size: 15px; flex-shrink: 0; }
.wa-async-text { flex: 1; }

.wa-async-slide-enter-active, .wa-async-slide-leave-active {
  transition: opacity .25s, transform .25s;
}
.wa-async-slide-enter-from, .wa-async-slide-leave-to {
  opacity: 0;
  transform: translateY(12px) scale(.96);
}
</style>
